// MetaLab.jsx
import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Upload,
  Zap,
  Target,
  Calculator,
  X,
  Plus,
  Sword,
  Shield,
  Heart,
  Wind,
  Sparkles,
  Skull,
  BarChart3,
  AlertCircle,
  Crosshair,
  RefreshCw,
  Cpu,
  Database,
  FileSpreadsheet,
  GitCompare,
  Users,
  TrendingUp,
  TrendingDown,
  SpiderChart,
  SpiderChartCard,
  VSScreen,
  HeroCard,
  HeroCardGrid,
  CompareBar,
  PatchDiffDashboard,
  DamageBreakdown,

} from "lucide-react";
import * as XLSX from "xlsx";
import heroList from "../data/heroList.json";
import HeroSelector from './HeroSelector';
import { EnhancedPatchCompareV2 } from './EnhancedPatchCompareV2';
import { EnhancedPatchCompareV3 } from './EnhancedPatchCompareV3';
// เพิ่มบรรทัดนี้หลัง import อื่นๆ
import {
  RadarChart,
  SkillComboCalculator,
  HeroCompareTab,
} from './MetaLabEnhancements';
import {
  computeTotalStatsRealisticRealistic,
  applyDefenseWithPenetration,
  STAT_LABELS as STAT_LABELS_NEW,
  formatStatValue,
  isPercentageStat,
} from './statsCalculator';
// ============================================
// Combat Simulator Imports
// ============================================
import {
  createHeroState,
  createGameState,
  runSimulation,
  buildActionQueue,
  getSimulationStats,
  tickByTickSimulation,
  DamageType,
  ActionType,
} from './combat-simulator/CombatEngine';

// ✅ เพิ่มบรรทัดนี้!
import CombatSimulatorTab from './combat-simulator/CombatSimulatorTab';
import BrandLogoBadge from "./ui/BrandLogoBadge";
import {
  ITEM_PASSIVES,
  attachPassivesToItems,
  applyAlwaysPassiveStats,
} from './combat-simulator/ItemPassives';

import {
  registerAllHeroPassives,
  getHeroPassiveInfo,
} from './combat-simulator/HeroPassives';
// ===================== Global CSS =====================
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700;900&family=Rajdhani:wght@400;500;600;700&display=swap');
    * { margin: 0; padding: 0; box-sizing: border-box; }
    html, body, #root {
      background-color: #000000 !important;
      color: #ffffff !important;
      overflow-x: hidden;
      font-family: 'Rajdhani', sans-serif;
      min-height: 100vh;
    }
    @keyframes scan { 0% { top: -10%; opacity: 0; } 50% { opacity: 1; } 100% { top: 110%; opacity: 0; } }
    @keyframes float { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-10px); } }
    @keyframes rotate { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.6; } }
    .scroll-container {
      scrollbar-width: thin;
      scrollbar-color: #ff1026 rgba(0, 0, 0, 0.5);
    }
    .scroll-container::-webkit-scrollbar { width: 8px; height: 8px; }
    .scroll-container::-webkit-scrollbar-track { background: rgba(0, 0, 0, 0.5); border-radius: 10px; }
    .scroll-container::-webkit-scrollbar-thumb {
      background: linear-gradient(to bottom, #ff1026, #a30013);
      border-radius: 10px;
      box-shadow: 0 0 10px rgba(255,16,38,0.6);
    }
    .scroll-container::-webkit-scrollbar-thumb:hover {
      background: linear-gradient(to bottom, #ff4d5f, #ff1026);
    }
    input[type="range"] {
      -webkit-appearance: none;
      appearance: none;
      width: 100%;
      height: 8px;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 5px;
      outline: none;
    }
    input[type="range"]::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 20px;
      height: 20px;
      background: linear-gradient(135deg, #ff1026, #f5f5f5);
      border-radius: 50%;
      cursor: pointer;
      box-shadow: 0 0 10px rgba(255,16,38,0.8);
    }
      @keyframes heroSelectorFadeIn {
  from { opacity: 0; transform: translateY(-10px) scale(0.95); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
    input[type="range"]::-moz-range-thumb { 
      width: 20px;
      height: 20px;
      background: linear-gradient(135deg, #ff1026, #f5f5f5);
      border-radius: 50%;
      cursor: pointer;
      border: none;
      box-shadow: 0 0 10px rgba(255,16,38,0.8);
    }
  `}</style>
);

// ===================== Helpers: hero images =====================
const normalizeName = (name = "") => name.toLowerCase().replace(/[^a-z0-9]/g, "");
const heroImageMap = new Map(heroList.map((h) => [normalizeName(h.name), h.image]));
const getHeroImageUrl = (heroName) => {
  const key = normalizeName(heroName);
  const file = heroImageMap.get(key);
  return file ? `${process.env.PUBLIC_URL}/heroimages/${file}` : null;
};

const FLOWBORN_PATCH_HEROES = [
  {
    name: "Flowborn Carry",
    role: "Carry",
    lane: "ADL",
    levels: {
      1: {
        name: "Flowborn Carry",
        level: 1,
        hp: 3400,
        phys_atk: 170,
        magic_power: 0,
        phys_def: 88,
        phys_def_pct: 0,
        magic_def: 50,
        magic_def_pct: 0,
        max_mana: 440,
        move_speed: 360,
        armor_pierce: 0,
        armor_pierce_pct: 0,
        magic_pierce: 0,
        magic_pierce_pct: 0,
        attack_speed: 0,
        crit_rate: 0,
        crit_dmg: 2,
        life_steal: 0,
        magic_life_steal: 0,
        cooldown_reduction: 0,
      },
      2: {
        name: "Flowborn Carry",
        level: 2,
        hp: 3620,
        phys_atk: 182,
        magic_power: 0,
        phys_def: 97,
        phys_def_pct: 0,
        magic_def: 58,
        magic_def_pct: 0,
        max_mana: 470,
        move_speed: 360,
        armor_pierce: 0,
        armor_pierce_pct: 0,
        magic_pierce: 0,
        magic_pierce_pct: 0,
        attack_speed: 0.02,
        crit_rate: 0,
        crit_dmg: 2,
        life_steal: 0,
        magic_life_steal: 0,
        cooldown_reduction: 0,
      },
    },
  },
  {
    name: "Flowborn Mage",
    role: "Mage",
    lane: "MID",
    levels: {
      1: {
        name: "Flowborn Mage",
        level: 1,
        hp: 3300,
        phys_atk: 150,
        magic_power: 0,
        phys_def: 86,
        phys_def_pct: 0,
        magic_def: 50,
        magic_def_pct: 0,
        max_mana: 490,
        move_speed: 350,
        armor_pierce: 0,
        armor_pierce_pct: 0,
        magic_pierce: 0,
        magic_pierce_pct: 0,
        attack_speed: 0,
        crit_rate: 0,
        crit_dmg: 2,
        life_steal: 0,
        magic_life_steal: 0,
        cooldown_reduction: 0,
      },
      2: {
        name: "Flowborn Mage",
        level: 2,
        hp: 3505,
        phys_atk: 158,
        magic_power: 0,
        phys_def: 94,
        phys_def_pct: 0,
        magic_def: 58,
        magic_def_pct: 0,
        max_mana: 525,
        move_speed: 350,
        armor_pierce: 0,
        armor_pierce_pct: 0,
        magic_pierce: 0,
        magic_pierce_pct: 0,
        attack_speed: 0.01,
        crit_rate: 0,
        crit_dmg: 2,
        life_steal: 0,
        magic_life_steal: 0,
        cooldown_reduction: 0,
      },
    },
  },
];

const addFlowbornPatchHeroes = (heroMap) => {
  FLOWBORN_PATCH_HEROES.forEach((hero) => {
    if (!heroMap.has(hero.name)) {
      heroMap.set(hero.name, {
        ...hero,
        levels: Object.fromEntries(
          Object.entries(hero.levels).map(([level, stats]) => [level, { ...stats }])
        ),
      });
    }
  });
};

// ===================== Helpers: item images =====================
const ITEM_CATEGORY_FOLDER = {
  Attack: "Physic",
  Physic: "Physic",
  Magic: "Magic",
  Defense: "Defense",
  Movement: "Movement",
  Jungling: "Jungling",
  Support: "Support",
};

const nameVariants = (name) => {
  const n = (name || "").trim();
  if (!n) return [];
  // รองรับ smart quote ↔ apostrophe
  const v1 = n.replace(/[’]/g, "'");
  const v2 = n.replace(/[']/g, "’");
  return Array.from(new Set([n, v1, v2]));
};

const getItemImageCandidates = (it) => {
  if (!it) return [];

  // ✅ ลองทั้ง base ปกติ + base ที่เผลอแตก zip ซ้อนชั้น
  const bases = [
    `${process.env.PUBLIC_URL}/itemimages`,
    `${process.env.PUBLIC_URL}/itemimages/itemimages`,
  ];

  const out = [];

  // 1) explicit image field (ของเดิมคุณ) — เก็บไว้
  if (it.image && String(it.image).trim()) {
    const f = String(it.image).trim().replace(/^\/+/, "");
    for (const base of bases) out.push(`${base}/${f}`);
  }

  const folder =
    ITEM_CATEGORY_FOLDER[it.category] ||
    ITEM_CATEGORY_FOLDER[(it.category || "").trim()] ||
    "Misc";

  const tier = Number.isFinite(+it.tier) ? +it.tier : null;
  const rawName = (it.name || "").trim();

  // ✅ tiersToTry: ลอง tier จากข้อมูลก่อน แล้วไล่ 1–3 กันข้อมูล tier ผิด
  const tiersToTry = Array.from(new Set([tier, 1, 2, 3].filter(Boolean)));

  // 2) ตามชื่อ + tier ในโฟลเดอร์ lv (ของเดิมคุณ) + เพิ่ม variants + เพิ่ม tiersToTry
  if (rawName) {
    for (const base of bases) {
      for (const t of tiersToTry) {
        for (const nv of nameVariants(rawName)) {
          const encName = encodeURIComponent(nv);
          out.push(`${base}/${folder}/lv${t}/${encName}.jpg`);
          out.push(`${base}/${folder}/lv${t}/${encName}.jpeg`);
          out.push(`${base}/${folder}/lv${t}/${encName}.png`);
          out.push(`${base}/${folder}/lv${t}/${encName}.webp`);
          // กันเคส extension ตัวใหญ่ (บางชุดไฟล์เป็น .JPG)
          out.push(`${base}/${folder}/lv${t}/${encName}.JPG`);
          out.push(`${base}/${folder}/lv${t}/${encName}.PNG`);
          out.push(`${base}/${folder}/lv${t}/${encName}.WEBP`);
        }
      }
    }
  }

  // 3) normalizeName fallback (ของเดิมคุณ) — เก็บไว้ + ลอง base ซ้อนชั้นด้วย
  const file = normalizeName(it.name || "");
  if (file) {
    for (const base of bases) {
      out.push(`${base}/${folder}/${file}.png`);
      out.push(`${base}/${folder}/${file}.jpg`);
      out.push(`${base}/${folder}/${file}.jpeg`);
      out.push(`${base}/${folder}/${file}.webp`);
      out.push(`${base}/${file}.png`);
      out.push(`${base}/${file}.jpg`);
      out.push(`${base}/${file}.jpeg`);
      out.push(`${base}/${file}.webp`);
      // เผื่อ extension ตัวใหญ่
      out.push(`${base}/${folder}/${file}.PNG`);
      out.push(`${base}/${folder}/${file}.JPG`);
      out.push(`${base}/${file}.PNG`);
      out.push(`${base}/${file}.JPG`);
    }
  }

  return Array.from(new Set(out));
};

const normalizeNameVariants = (name = "") => {
  const fixed = String(name)
    .trim()
    .replaceAll("’", "'")
    .replaceAll("‘", "'")
    .replaceAll("`", "'")
    .replaceAll("ΓÇÖ", "'"); // แก้ชื่อเพี้ยน

  const variants = new Set();

  variants.add(fixed);                     // Fafnir's Talon
  variants.add(fixed.replace(/'/g, ""));   // Fafnirs Talon
  variants.add(fixed.replace(/\s+/g, "_"));// Fafnir's_Talon
  variants.add(fixed.replace(/\s+/g, "-"));// Fafnir's-Talon
  variants.add(fixed.toLowerCase());       // fafnir's talon

  return [...variants];
};

// ===================== Constants =====================
const ROLES = {
  Fighter: { g: "linear-gradient(135deg, #f59e0b, #ea580c)", color: "#f59e0b", icon: Sword },
  Mage: { g: "linear-gradient(135deg, #8b5cf6, #7c3aed)", color: "#8b5cf6", icon: Zap },
  Tank: { g: "linear-gradient(135deg, #64748b, #475569)", color: "#64748b", icon: Shield },
  Support: { g: "linear-gradient(135deg, #10b981, #059669)", color: "#10b981", icon: Heart },
  Assassin: { g: "linear-gradient(135deg, #ef4444, #dc2626)", color: "#ef4444", icon: Skull },
  Assasin: { g: "linear-gradient(135deg, #ef4444, #dc2626)", color: "#ef4444", icon: Skull },
  Carry: { g: "linear-gradient(135deg, #0ea5e9, #0284c7)", color: "#0ea5e9", icon: Crosshair },
};

const ITEMS_CAT = {
  Attack: { g: "linear-gradient(135deg, #ef4444, #ea580c)", icon: Sword },
  Magic: { g: "linear-gradient(135deg, #8b5cf6, #7c3aed)", icon: Sparkles },
  Defense: { g: "linear-gradient(135deg, #64748b, #475569)", icon: Shield },
  Movement: { g: "linear-gradient(135deg, #10b981, #059669)", icon: Wind },
  Jungling: { g: "linear-gradient(135deg, #f59e0b, #eab308)", icon: Target },
  Support: { g: "linear-gradient(135deg, #ec4899, #f43f5e)", icon: Heart },
};

const LANES = { DSL: "⚔️", MID: "🔮", SUP: "💚", JUG: "🌲", ADL: "🎯" };
const STATS = {
  hp: { l: "HP", i: Heart, c: "#ef4444", m: 15000 },
  phys_atk: { l: "ATK", i: Sword, c: "#f59e0b", m: 600 },
  magic_power: { l: "AP", i: Zap, c: "#8b5cf6", m: 1200 },
  phys_def: { l: "DEF", i: Shield, c: "#64748b", m: 800 },
  magic_def: { l: "M.DEF", i: Shield, c: "#06b6d4", m: 500 },
  move_speed: { l: "SPD", i: Wind, c: "#10b981", m: 500 },
};

const SAMPLE_ITEMS = {
  Rankbreaker: { phys_atk: 100, armor_pierce_pct: 45, price: 2070 },
  Muramasa: { phys_atk: 80, armor_pierce_pct: 45, price: 2020 },
  "Fenrir's Tooth": { phys_atk: 200, price: 2950 },
  "Omni Arms": { phys_atk: 70, hp: 500, price: 2150 },
  "Claves Sancti": { phys_atk: 90, crit_rate: 25, price: 2250 },
  "Blade of Eternity": { phys_atk: 60, phys_def: 120, price: 2100 },
  "Frost Cape": { hp: 800, phys_def: 250, price: 2000 },
  "Hecate's Diadem": { magic_power: 400, price: 2200 },
  "Holy of Holies": { magic_power: 400, hp: 1400, price: 2950 },
  Apocalypse: { magic_power: 150, price: 2170 },
};
const BASE_FIELDS = ["hp", "phys_atk", "magic_power", "phys_def", "magic_def", "move_speed"];

// ===================== UI Components =====================
const Glow = ({ c, s = 400, x, y }) => (
  <div
    style={{
      position: "absolute",
      width: s,
      height: s,
      left: x,
      top: y,
      borderRadius: "50%",
      filter: "blur(100px)",
      background: `radial-gradient(circle, ${c}80 0%, ${c}40 30%, transparent 70%)`,
      animation: "pulse 4s ease-in-out infinite",
      pointerEvents: "none",
    }}
  />
);

const Grid = () => (
  <div
    style={{
      position: "absolute",
      inset: 0,
      backgroundImage:
        "linear-gradient(rgba(255,16,38,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,16,38,0.05) 1px, transparent 1px)",
      backgroundSize: "60px 60px",
      opacity: 0.2,
      pointerEvents: "none",
    }}
  />
);

const HexPattern = () => (
  <div
    style={{
      position: "absolute",
      inset: 0,
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0 L45 15 L45 45 L30 60 L15 45 L15 15 Z' fill='none' stroke='%23ff1026' stroke-width='1'/%3E%3C/svg%3E")`,
      backgroundSize: "60px 60px",
      opacity: 0.05,
      pointerEvents: "none",
    }}
  />
);

const ScanLine = () => (
  <div
    style={{
      position: "absolute",
      inset: 0,
      overflow: "hidden",
      pointerEvents: "none",
      zIndex: 10,
      borderRadius: "16px",
    }}
  >
    <div
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        height: "2px",
        background: "linear-gradient(90deg, transparent, #ff1026, transparent)",
        animation: "scan 3s linear infinite",
        boxShadow: "0 0 10px #ff1026",
      }}
    />
  </div>
);

const CornerAccents = ({ color = "#ff1026" }) => (
  <>
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "60px",
        height: "60px",
        borderTop: `3px solid ${color}`,
        borderLeft: `3px solid ${color}`,
        borderRadius: "16px 0 0 0",
        pointerEvents: "none",
        filter: `drop-shadow(0 0 5px ${color})`,
      }}
    />
    <div
      style={{
        position: "absolute",
        bottom: 0,
        right: 0,
        width: "60px",
        height: "60px",
        borderBottom: `3px solid ${color}`,
        borderRight: `3px solid ${color}`,
        borderRadius: "0 0 16px 0",
        pointerEvents: "none",
        filter: `drop-shadow(0 0 5px ${color})`,
      }}
    />
  </>
);

const Bar = ({ v, m, c, h = 8 }) => (
  <div
    style={{
      width: "100%",
      height: h,
      borderRadius: h / 2,
      overflow: "hidden",
      background: "rgba(0,0,0,0.6)",
      border: "1px solid rgba(255,16,38,0.2)",
      position: "relative",
    }}
  >
    <div
      style={{
        height: "100%",
        width: `${Math.min((v / m) * 100, 100)}%`,
        borderRadius: h / 2,
        background: `linear-gradient(90deg, ${c}, ${c}cc)`,
        boxShadow: `0 0 15px ${c}80`,
        transition: "all 0.5s",
      }}
    />
  </div>
);

const Card = ({ children, neon = false, style = {} }) => (
  <div
    style={{
      background: "rgba(0, 0, 0, 0.9)",
      backdropFilter: "blur(20px)",
      borderRadius: "16px",
      border: neon ? "2px solid #ff1026" : "1px solid rgba(255,16,38,0.2)",
      boxShadow: neon ? "0 0 30px rgba(255,16,38,0.4), inset 0 0 30px rgba(255,16,38,0.1)" : "none",
      position: "relative",
      transition: "all 0.3s",
      ...style,
    }}
  >
    {children}
  </div>
);

const Button = ({ children, onClick, variant = "primary", style = {}, disabled = false }) => {
  const [hover, setHover] = useState(false);

  const baseStyle = {
    padding: "12px 24px",
    borderRadius: "12px",
    border: "none",
    fontWeight: "bold",
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "all 0.3s",
    fontFamily: "'Rajdhani', sans-serif",
    fontSize: "14px",
    ...style,
  };

  const variants = {
    primary: {
      background: hover && !disabled ? "linear-gradient(135deg, #ff4d5f, #ff1026)" : "linear-gradient(135deg, #ff1026, #a30013)",
      color: "#ffffff",
      boxShadow: hover && !disabled ? "0 0 30px rgba(255,16,38,0.6)" : "0 0 20px rgba(255,16,38,0.4)",
      opacity: disabled ? 0.5 : 1,
    },
    secondary: {
      background: hover && !disabled ? "rgba(0, 0, 0, 0.4)" : "rgba(0, 0, 0, 0.6)",
      color: hover && !disabled ? "#ffffff" : "#9ca3af",
      border: hover && !disabled ? "2px solid #ff1026" : "2px solid rgba(255,16,38,0.3)",
      opacity: disabled ? 0.5 : 1,
    },
    danger: {
      background: hover && !disabled ? "rgba(239, 68, 68, 0.2)" : "rgba(239, 68, 68, 0.1)",
      color: hover && !disabled ? "#ffffff" : "#fca5a5",
      border: hover && !disabled ? "2px solid rgba(239, 68, 68, 0.7)" : "2px solid rgba(239, 68, 68, 0.4)",
      opacity: disabled ? 0.5 : 1,
    },
  };

  return (
    <button
      onClick={disabled ? undefined : onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ ...baseStyle, ...(variants[variant] || variants.primary) }}
    >
      {children}
    </button>
  );
};

const Input = ({ value, onChange, placeholder, style = {}, icon = null }) => (
  <div style={{ position: "relative", ...style }}>
    {icon && (
      <div
        style={{
          position: "absolute",
          left: "12px",
          top: "50%",
          transform: "translateY(-50%)",
          color: "#ff1026",
          display: "flex",
          alignItems: "center",
        }}
      >
        {icon}
      </div>
    )}
    <input
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      style={{
        width: "100%",
        padding: icon ? "10px 12px 10px 40px" : "10px 12px",
        background: "rgba(0, 0, 0, 0.6)",
        border: "2px solid rgba(255,16,38,0.3)",
        borderRadius: "12px",
        color: "#ffffff",
        fontSize: "14px",
        outline: "none",
        transition: "all 0.3s",
        fontFamily: "'Rajdhani', sans-serif",
      }}
      onFocus={(e) => (e.target.style.borderColor = "#ff1026")}
      onBlur={(e) => (e.target.style.borderColor = "rgba(255,16,38,0.3)")}
    />
  </div>
);

const Select = ({ value, onChange, options, style = {}, icon = null }) => (
  <div style={{ position: "relative", ...style }}>
    {icon && (
      <div
        style={{
          position: "absolute",
          left: "12px",
          top: "50%",
          transform: "translateY(-50%)",
          color: "#ff1026",
          display: "flex",
          alignItems: "center",
          pointerEvents: "none",
        }}
      >
        {icon}
      </div>
    )}
    <select
      value={value}
      onChange={onChange}
      style={{
        width: "100%",
        padding: icon ? "10px 12px 10px 40px" : "10px 12px",
        background: "rgba(0, 0, 0, 0.6)",
        border: "2px solid rgba(255,16,38,0.3)",
        borderRadius: "12px",
        color: "#ffffff",
        fontSize: "14px",
        outline: "none",
        transition: "all 0.3s",
        fontFamily: "'Rajdhani', sans-serif",
        appearance: "none",
      }}
      onFocus={(e) => (e.target.style.borderColor = "#ff1026")}
      onBlur={(e) => (e.target.style.borderColor = "rgba(255,16,38,0.3)")}
    >
      {options.map((o) => (
        <option key={String(o.value)} value={o.value} style={{ background: "#000" }}>
          {o.label}
        </option>
      ))}
    </select>
  </div>
);

// ===================== Core math (ใช้ร่วมกันทุกฟีเจอร์) =====================
const STAT_LABELS = {
  hp: "HP",
  phys_atk: "ATK",
  magic_power: "AP",
  phys_def: "Phys DEF",
  phys_def_pct: "Phys DEF %",
  magic_def: "Magic DEF",
  magic_def_pct: "Magic DEF %",
  move_speed: "Move Speed",
  max_mana: "Max Mana",
  armor_pierce: "Armor Pierce",
  armor_pierce_pct: "Armor Pierce %",
  magic_pierce: "Magic Pierce",
  magic_pierce_pct: "Magic Pierce %",
  attack_speed: "Attack Speed %",
  crit_rate: "Crit Rate %",
  crit_dmg: "Crit Damage %",
  life_steal: "Life Steal %",
  magic_life_steal: "Magic Life Steal %",
  cooldown_reduction: "CDR %",
  hp_regen: "HP Regen",
  mana_regen: "Mana Regen",
  move_speed_pct: "Move Speed %",
};
const getAllStatKeys = (obj) => {
  const fromLabels = Object.keys(STAT_LABELS);
  const fromObj = Object.keys(obj || {}).filter(
    (k) => typeof obj?.[k] === "number"
  );
  return Array.from(new Set([...fromLabels, ...fromObj]))
    .sort((a, b) => a.localeCompare(b));
};
// =====================================================================
// โค้ดที่ต้องเพิ่มใน MetaLab.jsx
// วางไว้หลัง STAT_LABELS และก่อน computeTotalStats เดิม
// =====================================================================

// Stats ที่เป็น percentage (บวกรวมกันเป็น % โดยตรง)
const PERCENTAGE_STATS = new Set([
  'attack_speed',
  'crit_rate',
  'crit_dmg',
  'life_steal',
  'magic_life_steal',
  'cooldown_reduction',
  'armor_pierce_pct',
  'magic_pierce_pct',
  'spell_vamp',
]);

// Stats ที่เป็น % ของ base stat อื่น (ต้องคูณ)
const PERCENTAGE_OF_BASE = {
  'hp_pct': 'hp',
  'phys_def_pct': 'phys_def',
  'magic_def_pct': 'magic_def',
  'move_speed_pct': 'move_speed',
  'phys_atk_pct': 'phys_atk',
  'magic_power_pct': 'magic_power',
};

/**
 * รวม stats จาก items แยกตามประเภท
 */
const sumItemStatsByType = (items = []) => {
  const result = {
    flat: {},      // stats ที่บวกตรงๆ
    pct: {},       // stats ที่เป็น % โดยตรง
    pctOfBase: {}, // stats ที่เป็น % ของ base
  };

  for (const item of items) {
    const stats = item?.stats || {};

    for (const [key, value] of Object.entries(stats)) {
      const v = +value || 0;
      if (v === 0) continue;

      if (PERCENTAGE_OF_BASE[key]) {
        result.pctOfBase[key] = (result.pctOfBase[key] || 0) + v;
      } else if (PERCENTAGE_STATS.has(key)) {
        result.pct[key] = (result.pct[key] || 0) + v;
      } else {
        result.flat[key] = (result.flat[key] || 0) + v;
      }
    }
  }

  return result;
};

/**
 * คำนวณ total stats แบบสมจริง (flat + percentage)
 */
/**
 * คำนวณ total stats แบบสมจริง + Arcana + Item Passives
 */
const computeTotalStatsRealistic = (heroLevelObj, items = [], arcanaSet = { red: null, purple: null, green: null }) => {
  const base = { ...(heroLevelObj || {}) };

  // 1. รวม Stats จาก Items
  const itemStats = sumItemStatsByType(items);

  // 2. รวม Stats จาก Arcana (คูณ 10 ชิ้นต่อสี)
  // สมมติ arcanaSet ส่งมาเป็น object ของ Arcana Object
  const arcanaStats = { flat: {}, pct: {} };
  const processArcana = (arcanaObj) => {
    if (!arcanaObj || !arcanaObj.stats) return;
    // คูณ 10 ชิ้น (Standard RoV Build)
    const QTY = 10;
    for (const [k, v] of Object.entries(arcanaObj.stats)) {
      if (PERCENTAGE_STATS.has(k)) {
        arcanaStats.pct[k] = (arcanaStats.pct[k] || 0) + (v * QTY);
      } else {
        arcanaStats.flat[k] = (arcanaStats.flat[k] || 0) + (v * QTY);
      }
    }
  };
  processArcana(arcanaSet.red);
  processArcana(arcanaSet.purple);
  processArcana(arcanaSet.green);

  // เริ่มคำนวณ Total
  const total = {};

  // รวม keys ทั้งหมด
  const allKeys = new Set([
    ...Object.keys(base),
    ...Object.keys(itemStats.flat),
    ...Object.keys(itemStats.pct),
    ...Object.keys(arcanaStats.flat),
    ...Object.keys(arcanaStats.pct),
  ]);

  // Pass 1: คำนวณ Stat พื้นฐาน (Base + ItemFlat + ArcanaFlat) * Multiplier
  for (const key of allKeys) {
    const baseVal = +base[key] || 0;
    const itemFlat = +itemStats.flat[key] || 0;
    const arcanaFlat = +arcanaStats.flat[key] || 0;
    const itemPct = +itemStats.pct[key] || 0;
    const arcanaPct = +arcanaStats.pct[key] || 0;

    // หา % Bonus Base (เช่น %HP)
    const pctOfBaseKey = Object.entries(PERCENTAGE_OF_BASE).find(([p, b]) => b === key)?.[0];
    const pctOfBaseVal = pctOfBaseKey ? (+itemStats.pctOfBase[pctOfBaseKey] || 0) : 0;

    if (PERCENTAGE_STATS.has(key)) {
      // พวก % Cri Rate, CDR รวมกันตรงๆ
      total[key] = baseVal + itemPct + arcanaPct;
    } else {
      // พวก HP, ATK: (Base + Flat) * (1 + Bonus%)
      let val = baseVal + itemFlat + arcanaFlat;
      if (pctOfBaseVal !== 0) {
        val = val * (1 + pctOfBaseVal / 100);
      }
      total[key] = val;
    }
  }

  // Pass 2: Hardcode Item Unique Passives (ส่วนที่เพิ่มความแม่นยำ)
  const itemNames = items.map(it => it.name.toLowerCase());

  // --- Hecate's Diadem (พลังเวท +35%) ---
  if (itemNames.some(n => n.includes("hecate"))) {
    total.magic_power = (total.magic_power || 0) * 1.35;
  }

  // --- Fenrir's Tooth (ใน Stat Sheet จะเห็นแค่ ATK 200 แต่ Passive ทำงานตอนคำนวณดาเมจ) ---
  // (Logic Fenrir จะไปอยู่ที่ DamageCalculator แทน)

  return {
    base,
    flat: itemStats.flat, // ไว้ดู debug
    arcana: arcanaStats,  // ไว้ดู debug
    total,
  };
};

/**
 * คำนวณ damage หลังหัก defense พร้อม penetration
 */

// =====================================================================
// จบโค้ดที่ต้องเพิ่ม
// =====================================================================

const getNumericStatKeys = (obj) =>
  Object.keys(obj || {})
    .filter((k) => typeof obj[k] === "number" && obj[k] !== 0)
    .sort((a, b) => a.localeCompare(b));

const HERO_LEVEL_CAP = 15;
const HERO_STAT_KEYS = [
  "hp",
  "phys_atk",
  "magic_power",
  "phys_def",
  "phys_def_pct",
  "magic_def",
  "magic_def_pct",
  "max_mana",
  "move_speed",
  "armor_pierce",
  "armor_pierce_pct",
  "magic_pierce",
  "magic_pierce_pct",
  "attack_speed",
  "crit_rate",
  "crit_dmg",
  "life_steal",
  "magic_life_steal",
  "cooldown_reduction",
];

const isEmptyHeroLevel = (lvl) =>
  HERO_STAT_KEYS.every((k) => (+lvl?.[k] || 0) === 0);

const fillMissingHeroLevels = (hero) => {
  const levels = hero?.levels || {};
  const base1 = levels[1];
  const base2 = levels[2];
  if (!base1 || !base2) return;

  const growth = {};
  for (const k of HERO_STAT_KEYS) {
    growth[k] = (+base2[k] || 0) - (+base1[k] || 0);
  }

  for (let lv = 1; lv <= HERO_LEVEL_CAP; lv++) {
    const existing = levels[lv];
    if (!existing || isEmptyHeroLevel(existing)) {
      const filled = { ...base1, level: lv };
      for (const k of HERO_STAT_KEYS) {
        filled[k] = (+base1[k] || 0) + growth[k] * (lv - 1);
      }
      levels[lv] = filled;
    }
  }
};


const applyDefense = (raw, def) => {
  const D = Math.max(0, +def || 0);
  return raw * (600 / (600 + D));
};

const computeSkillRawDamage = ({ base = 0, adScaling = 0, apScaling = 0, attackerStats }) => {
  const atk = +attackerStats?.phys_atk || 0;
  const ap = +attackerStats?.magic_power || 0;
  return (+base || 0) + (+adScaling || 0) * atk + (+apScaling || 0) * ap;
};

// ===================== UploadScreen =====================
const UploadScreen = ({ onLoad, patches = [] }) => {
  const [drag, setDrag] = useState(false);
  const [loading, setLoading] = useState(false);
  const [prog, setProg] = useState(0);
  const [err, setErr] = useState(null);
  const [name, setName] = useState("");
  const ref = useRef();

  const parse = async (f) => {
    setLoading(true);
    setErr(null);
    setProg(0);
    try {
      const buf = await f.arrayBuffer();
      setProg(20);
      const wb = XLSX.read(buf, { type: "array" });
      setProg(40);
      const d = { heroes: [], skills: [], items: [], runes: [] };

      // Heroes
      // Heroes (add full stats from public/metalab/rov-database.xlsx)
      // === แก้ไขส่วนอ่าน Heroes ในฟังก์ชัน parse ===
      if (wb.SheetNames.includes("Heroes")) {
        const j = XLSX.utils.sheet_to_json(wb.Sheets["Heroes"], { header: 1 });
        for (let i = 2; i < j.length; i++) {
          const r = j[i];

          // 1. ล้างชื่อฮีโร่ให้สะอาด (ลบเว้นวรรคหน้า-หลัง)
          const rawName = String(r[0] || "").trim();

          // ข้ามถ้าไม่มีชื่อ หรือเป็นบรรทัด Header ภาษาไทย
          if (!rawName || rawName === "hero_name" || rawName === "ชื่อฮีโร่") continue;

          d.heroes.push({
            name: rawName, // ใช้ชื่อที่ trim แล้ว
            role: String(r[1] || "").trim(),
            lane: String(r[2] || "").trim(),
            level: parseInt(String(r[3]).trim()) || 1,
            hp: parseFloat(r[4]) || 0,
            phys_atk: parseFloat(r[6]) || 0,
            magic_power: parseFloat(r[8]) || 0,
            phys_def: parseFloat(r[9]) || 0,
            phys_def_pct: parseFloat(r[11]) || 0,
            magic_def: parseFloat(r[13]) || 0,
            magic_def_pct: parseFloat(r[15]) || 0,
            max_mana: parseFloat(r[17]) || 0,
            move_speed: parseFloat(r[19]) || 0,
            armor_pierce: parseFloat(r[20]) || 0,
            armor_pierce_pct: parseFloat(r[21]) || 0,
            magic_pierce: parseFloat(r[22]) || 0,
            magic_pierce_pct: parseFloat(r[23]) || 0,
            attack_speed: parseFloat(r[24]) || 0,
            crit_rate: parseFloat(r[25]) || 0,
            crit_dmg: parseFloat(r[26]) || 0,
            life_steal: parseFloat(r[27]) || 0,
            magic_life_steal: parseFloat(r[28]) || 0,
            cooldown_reduction: parseFloat(r[29]) || 0,
          });
        }
      }

      setProg(55);

      // Skills
      if (wb.SheetNames.includes("Skills")) {
        const j = XLSX.utils.sheet_to_json(wb.Sheets["Skills"], { header: 1 });
        for (let i = 2; i < j.length; i++) {
          const r = j[i];
          r[0] &&
            r[2] &&
            d.skills.push({
              hero: r[0],
              slot: r[1] || "",
              name: r[2] || "",
              type: r[3] || "",
              damage_type: String(r[4] || ""),
              description: r[5] || "",
              base_dmg: [r[6], r[7], r[8], r[9], r[10], r[11]].map((v) => +v || 0),
              ad_scaling: +r[12] || 0,
              ap_scaling: +r[13] || 0,
              cooldown: [r[14], r[15], r[16], r[17], r[18], r[19]].map((v) => +v || 0),
              mana_cost: [r[20], r[21], r[22], r[23], r[24], r[25]].map((v) => +v || 0),
              range: r[26] || "",
              cc_type: r[27] || "",
              cc_duration: +r[28] || 0,
              additional_effects: r[29] || "",
            });
        }
      }
      setProg(70);
      // ===================== Items (FIXED - ใช้ fixed column index) =====================

      // ===================== Arcana (NEW) =====================
      // เปลี่ยนจาก:
      // เป็น:
      if (wb.SheetNames.includes("Arcana")) {
        const sheet = wb.Sheets["Arcana"];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });

        const parseNum = (v) => {
          if (typeof v === "number") return v;
          const s = String(v || "").replace(/[+%]/g, "").trim();
          return parseFloat(s) || 0;
        };

        const addStat = (stats, key, val) => { if (val > 0) stats[key] = val; };

        for (let i = 2; i < rows.length; i++) { // เริ่มแถว 2 (skip header)
          const r = rows[i];
          if (!r[0]) continue;

          const stats = {};
          // Column mapping ตาม Excel ใหม่ (Arcana sheet)
          addStat(stats, 'hp', parseNum(r[4]));           // Column E
          addStat(stats, 'phys_atk', parseNum(r[6]));     // Column G  
          addStat(stats, 'magic_power', parseNum(r[8]));  // Column I
          addStat(stats, 'phys_def', parseNum(r[10]));    // Column K
          addStat(stats, 'magic_def', parseNum(r[12]));   // Column M
          addStat(stats, 'move_speed_pct', parseNum(r[15])); // Column P
          addStat(stats, 'armor_pierce', parseNum(r[16]));   // Column Q
          addStat(stats, 'magic_pierce', parseNum(r[18]));   // Column S
          addStat(stats, 'attack_speed', parseNum(r[20]));   // Column U
          addStat(stats, 'crit_rate', parseNum(r[21]));      // Column V
          addStat(stats, 'crit_dmg', parseNum(r[22]));       // Column W
          addStat(stats, 'life_steal', parseNum(r[23]));     // Column X
          addStat(stats, 'magic_life_steal', parseNum(r[24])); // Column Y
          addStat(stats, 'cooldown_reduction', parseNum(r[25])); // Column Z

          d.runes.push({
            type: "ARCANA",
            name: r[0],              // Column A: rune_name
            tier: parseNum(r[2]),    // Column C: tier
            color: r[1],             // Column B: Color (Red/Purple/Green)
            desc: r[3],              // Column D: description
            stats: stats
          });
        }
      }
      if (wb.SheetNames.includes("Items")) {
        const sheet = wb.Sheets["Items"];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
        if (!rows.length) return;

        const parseNum = (v) => {
          if (v === null || v === undefined || v === "") return 0;
          if (typeof v === "number") return v;
          const s = String(v).trim();
          if (s.includes("/")) return parseFloat(s.split("/")[0]) || 0;
          const cleaned = s.replace(/[+%]/g, "").trim();
          const n = parseFloat(cleaned);
          return Number.isFinite(n) ? n : 0;
        };

        // Fixed column index ตาม Excel จริง
        const COL = {
          name: 0, category: 1, tier: 2, price: 3, sell_price: 4, build_from: 5,
          hp: 6, passive_inline: 7, phys_atk: 8, attack_speed: 9, critical_rate: 10,
          life_steal: 11, magic_power: 12, phys_defense: 13, magic_defense: 14,
          max_mana: 15, movement_speed: 16, movement_speed_pct: 17,
          armor_pierce: 18, armor_pierce_pct: 19, magic_pierce: 20, magic_pierce_pct: 21,
          cooldown_reduction: 27, hp_regen: 28, mana_regen: 29,
          passive_name: 30, passive_desc: 31,
        };

        for (let i = 2; i < rows.length; i++) {
          const r = rows[i];
          const name = String(r[COL.name] || "").trim();
          if (!name) continue;

          // แปลง % เป็นตัวเลขปกติ (0.35 -> 35)
          const pct = (v) => { const n = parseNum(v); return n < 1 && n > 0 ? n * 100 : n; };

          const stats = {
            hp: parseNum(r[COL.hp]),
            phys_atk: parseNum(r[COL.phys_atk]),
            attack_speed: pct(r[COL.attack_speed]),
            crit_rate: pct(r[COL.critical_rate]),
            life_steal: pct(r[COL.life_steal]),
            magic_power: parseNum(r[COL.magic_power]),
            phys_def: parseNum(r[COL.phys_defense]),
            magic_def: parseNum(r[COL.magic_defense]),
            max_mana: parseNum(r[COL.max_mana]),
            move_speed: parseNum(r[COL.movement_speed]),
            move_speed_pct: pct(r[COL.movement_speed_pct]),
            armor_pierce: parseNum(r[COL.armor_pierce]),
            armor_pierce_pct: pct(r[COL.armor_pierce_pct]),
            magic_pierce: parseNum(r[COL.magic_pierce]),
            magic_pierce_pct: pct(r[COL.magic_pierce_pct]),
            cooldown_reduction: pct(r[COL.cooldown_reduction]),
            hp_regen: parseNum(r[COL.hp_regen]),
            mana_regen: parseNum(r[COL.mana_regen]),
          };

          const passiveInline = String(r[COL.passive_inline] || "").trim();
          const passiveDesc = String(r[COL.passive_desc] || "").trim();

          d.items.push({
            name,
            category: String(r[COL.category] || "").trim(),
            tier: parseNum(r[COL.tier]) || 1,
            price: parseNum(r[COL.price]),
            sell_price: parseNum(r[COL.sell_price]),
            build_from: String(r[COL.build_from] || "").trim(),
            image: "",
            stats,
            passive_name: String(r[COL.passive_name] || "").trim(),
            passive: passiveInline || passiveDesc,
          });
        }
      }
      setProg(85);

      // Runes
      if (wb.SheetNames.includes("Runes")) {
        const j = XLSX.utils.sheet_to_json(wb.Sheets["Runes"], { header: 1 });
        for (let i = 2; i < j.length; i++) {
          const r = j[i];
          r[0] &&
            d.runes.push({
              name: r[0],
              tree: r[1] || "",
              tier: +r[2] || 1,
              desc: r[3] || "",
            });
        }
      }
      setProg(95);

      // Group heroes by name -> levels map
      // ค้นหาส่วน // Group heroes by name -> levels map ใน MetaLab.jsx
      const hm = new Map();
      d.heroes.forEach((h) => {
        // บังคับให้ชื่อสะอาดอีกรอบก่อนเข้า Map
        const cleanName = h.name.trim();

        if (!hm.has(cleanName)) {
          hm.set(cleanName, {
            name: cleanName,
            role: h.role,
            lane: h.lane,
            levels: {} // สร้าง Object เปล่าเพื่อรอรับเลเวลต่างๆ
          });
        }

        // ยัดข้อมูลเลเวลลงไป เช่น hm.get("Airi").levels[1] = { ข้อมูลเลเวล 1 }
        hm.get(cleanName).levels[h.level] = { ...h };
      });
      addFlowbornPatchHeroes(hm);

      // เติมข้อมูลเลเวลที่ว่างด้วยการคำนวณจาก Lv1/Lv2 (กรณีใน Excel มีเฉพาะ 1-2)
      hm.forEach((hero) => fillMissingHeroLevels(hero));

      const patchName = name?.trim() ? name.trim() : f.name.replace(/\.xlsx$/i, "").replace(/\.xls$/i, "");
      const pd = {
        heroes: [...hm.values()],
        skills: d.skills,
        items: d.items,
        runes: d.runes,
        meta: {
          heroCount: hm.size,
          skillCount: d.skills.length,
          itemCount: d.items.length,
          runeCount: d.runes.length,
          patchName,
        },
      };

      setProg(100);
      setTimeout(() => {
        setLoading(false);
        onLoad(pd);
      }, 300);
    } catch (e) {
      console.error(e);
      setErr("Cannot read file");
      setLoading(false);
    }
  };

  const drop = (e) => {
    e.preventDefault();
    setDrag(false);
    const f = e.dataTransfer.files[0];
    if (!f) return;
    const ok = f.name.endsWith(".xlsx") || f.name.endsWith(".xls");
    ok ? parse(f) : setErr("Please upload .xlsx/.xls file");
  };

  return (
    <>
      <GlobalStyles />
      <div
        style={{
          minHeight: "100vh",
          background: "#000000",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "32px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div style={{ position: "fixed", inset: 0, pointerEvents: "none" }}>
          <Glow c="#ff1026" s={800} x="-20%" y="-10%" />
          <Glow c="#ff4d5f" s={600} x="80%" y="70%" />
          <Glow c="#f5f5f5" s={400} x="50%" y="50%" />
          <Grid />
          <HexPattern />
        </div>

        <div style={{ position: "relative", zIndex: 10, width: "100%", maxWidth: "900px" }}>
          <div style={{ textAlign: "center", marginBottom: "28px" }}>
            <div style={{ display: "inline-block", position: "relative", marginBottom: "14px" }}>
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "90px",
                  height: "90px",
                  left: "-7px",
                  top: "-7px",
                  border: "4px solid rgba(255,16,38,0.2)",
                  borderRadius: "50%",
                  animation: "rotate 20s linear infinite",
                }}
              />
              <div
                style={{
                  position: "relative",
                  width: "76px",
                  height: "76px",
                  background: "linear-gradient(135deg, #ff1026, #f5f5f5)",
                  borderRadius: "24px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 0 60px rgba(255,16,38,0.8)",
                  animation: "float 3s ease-in-out infinite",
                }}
              >
                <BrandLogoBadge size={76} radius={20} />
              </div>
            </div>

            <h1
              style={{
                fontFamily: "'Orbitron', sans-serif",
                fontSize: "3.2rem",
                fontWeight: 900,
                background: "linear-gradient(to right, #ff1026, #f5f5f5, #ff1026)",
                backgroundSize: "200% auto",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                marginBottom: "10px",
                letterSpacing: "0.22em",
                filter: "drop-shadow(0 0 30px rgba(255,16,38,0.8))",
              }}
            >
              META LAB
            </h1>

            <p style={{ color: "#6b7280", fontSize: "0.9rem", letterSpacing: "0.1em" }}>
              ADVANCED ROV DATA ANALYSIS SYSTEM
            </p>
          </div>

          <Card style={{ padding: "18px", marginBottom: "16px" }}>
            <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
              <FileSpreadsheet size={18} color="#ff1026" />
              <div style={{ flex: 1 }}>
                <div style={{ color: "#9ca3af", fontWeight: "bold", marginBottom: "6px" }}>
                  Patch Name (optional)
                </div>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="เช่น 1.2.3 (ถ้าไม่ใส่จะใช้ชื่อไฟล์)" />
              </div>
            </div>
          </Card>

          {loading ? (
            <Card neon style={{ padding: "32px" }}>
              <div style={{ textAlign: "center", marginBottom: "18px" }}>
                <div
                  style={{
                    fontFamily: "'Orbitron', sans-serif",
                    fontSize: "4rem",
                    fontWeight: 900,
                    background: "linear-gradient(to right, #ff1026, #f5f5f5)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                    marginBottom: "10px",
                    animation: "pulse 1s ease-in-out infinite",
                    filter: "drop-shadow(0 0 30px rgba(255,16,38,0.8))",
                  }}
                >
                  {prog}%
                </div>
                <p style={{ color: "#9ca3af", fontSize: "1.05rem", letterSpacing: "0.15em" }}>
                  PROCESSING GAME DATA...
                </p>
              </div>

              <div
                style={{
                  position: "relative",
                  height: "16px",
                  background: "rgba(0, 0, 0, 0.8)",
                  borderRadius: "999px",
                  overflow: "hidden",
                  border: "2px solid rgba(255,16,38,0.5)",
                  boxShadow: "inset 0 0 20px rgba(0, 0, 0, 0.8)",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${prog}%`,
                    background: "linear-gradient(90deg, #ff1026, #ff4d5f, #f5f5f5)",
                    transition: "width 0.25s",
                    boxShadow: "0 0 20px rgba(255,16,38,1)",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                      animation: "pulse 1s ease-in-out infinite",
                    }}
                  />
                </div>
              </div>
            </Card>
          ) : err ? (
            <Card
              style={{
                padding: "32px",
                border: "2px solid rgba(239, 68, 68, 0.5)",
                boxShadow: "0 0 30px rgba(239, 68, 68, 0.3)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "16px", color: "#f87171", marginBottom: "16px" }}>
                <AlertCircle size={32} />
                <span style={{ fontWeight: "bold", fontSize: "1.25rem" }}>SYSTEM ERROR</span>
              </div>
              <p style={{ color: "#9ca3af", marginBottom: "24px", fontFamily: "monospace" }}>{err}</p>
              <Button
                onClick={() => {
                  setErr(null);
                  ref.current?.click();
                }}
              >
                RETRY UPLOAD
              </Button>
            </Card>
          ) : (
            <>
              <Card
                neon
                style={{
                  padding: "56px",
                  textAlign: "center",
                  cursor: "pointer",
                  transform: drag ? "scale(1.03)" : "scale(1)",
                  background: drag ? "rgba(255,16,38,0.2)" : "rgba(0, 0, 0, 0.9)",
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDrag(true);
                }}
                onDragLeave={() => setDrag(false)}
                onDrop={drop}
                onClick={() => ref.current?.click()}
              >
                <ScanLine />
                <CornerAccents />

                <Upload
                  size={64}
                  color="#ff1026"
                  style={{
                    margin: "0 auto 18px",
                    animation: "float 3s ease-in-out infinite",
                    filter: "drop-shadow(0 0 20px rgba(255,16,38,0.6))",
                  }}
                />

                <h3
                  style={{
                    fontFamily: "'Orbitron', sans-serif",
                    fontSize: "1.6rem",
                    fontWeight: 900,
                    color: "#ffffff",
                    marginBottom: "10px",
                    letterSpacing: "0.12em",
                  }}
                >
                  DROP EXCEL FILE
                </h3>
                <p style={{ color: "#9ca3af", marginBottom: "22px", fontSize: "1.05rem" }}>
                  or click to browse your system
                </p>

                <input
                  ref={ref}
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    f && parse(f);
                  }}
                />

                <Button
                  onClick={() => ref.current?.click()}
                  style={{
                    padding: "14px 36px",
                    fontSize: "1.05rem",
                    fontFamily: "'Orbitron', sans-serif",
                    backgroundSize: "200% auto",
                  }}
                >
                  SELECT FILE
                </Button>
              </Card>

              {patches.length > 0 && (
                <div
                  style={{
                    marginTop: "18px",
                    padding: "18px",
                    background:
                      "linear-gradient(90deg, rgba(255,16,38,0.1), rgba(255,16,38,0.05), transparent)",
                    border: "1px solid rgba(255,16,38,0.3)",
                    borderRadius: "16px",
                  }}
                >
                  <p
                    style={{
                      color: "#9ca3af",
                      fontSize: "0.875rem",
                      marginBottom: "12px",
                      textTransform: "uppercase",
                      letterSpacing: "0.1em",
                    }}
                  >
                    Previously Loaded:
                  </p>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                    {patches.map((p, i) => (
                      <span
                        key={i}
                        style={{
                          padding: "8px 14px",
                          background: "rgba(0, 0, 0, 0.6)",
                          border: "1px solid rgba(255,16,38,0.5)",
                          borderRadius: "8px",
                          color: "#ff4d5f",
                          fontWeight: "bold",
                          boxShadow: "0 0 15px rgba(255,16,38,0.2)",
                        }}
                      >
                        {p.meta.patchName}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

// ===================== Tabs =====================
const TabBtn = ({ active, icon: Icon, label, onClick }) => (
  <button
    onClick={onClick}
    style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "10px 14px",
      borderRadius: 12,
      background: active ? "linear-gradient(135deg, rgba(255,16,38,0.35), rgba(245,245,245,0.1))" : "rgba(0,0,0,0.55)",
      border: active ? "2px solid rgba(255,16,38,0.6)" : "2px solid rgba(255,16,38,0.25)",
      color: active ? "#fff" : "#9ca3af",
      cursor: "pointer",
      fontFamily: "'Rajdhani', sans-serif",
      fontWeight: "bold",
      boxShadow: active ? "0 0 20px rgba(255,16,38,0.25)" : "none",
      transition: "all 0.25s",
      whiteSpace: "nowrap",
    }}
  >
    <Icon size={18} color={active ? "#ffd7dc" : "#ff1026"} />
    {label}
  </button>
);

// ===================== Patch Compare =====================
const diffHeroes = (a, b, level = 1) => {
  const ma = new Map(a.heroes.map((h) => [h.name, h]));
  const mb = new Map(b.heroes.map((h) => [h.name, h]));
  const added = [];
  const removed = [];
  const changed = [];

  for (const [name, hb] of mb) {
    if (!ma.has(name)) added.push(name);
    else {
      const ha = ma.get(name);
      const sa = ha.levels[level] || ha.levels[1] || Object.values(ha.levels)[0] || {};
      const sb = hb.levels[level] || hb.levels[1] || Object.values(hb.levels)[0] || {};
      const fields = ["hp", "phys_atk", "magic_power", "phys_def", "magic_def", "move_speed", "role", "lane"];
      const diffs = fields
        .map((f) => {
          const va = f in sa ? sa[f] : ha[f];
          const vb = f in sb ? sb[f] : hb[f];
          return va !== vb ? { f, a: va, b: vb, d: (typeof va === "number" && typeof vb === "number") ? vb - va : null } : null;
        })
        .filter(Boolean);
      if (diffs.length) changed.push({ name, diffs });
    }
  }

  for (const [name] of ma) if (!mb.has(name)) removed.push(name);
  return { added, removed, changed };
};

const PatchCompareTab = ({ patches, aIdx, bIdx, setAIdx, setBIdx }) => {
  const [level, setLevel] = useState(1);
  const [q, setQ] = useState("");

  const canCompare = patches.length >= 2 && patches[aIdx] && patches[bIdx] && aIdx !== bIdx;
  const diff = useMemo(() => (canCompare ? diffHeroes(patches[aIdx], patches[bIdx], level) : null), [canCompare, patches, aIdx, bIdx, level]);

  const options = patches.map((p, i) => ({ value: i, label: `${i + 1}. ${p.meta.patchName}` }));

  const filteredChanged = useMemo(() => {
    if (!diff) return [];
    if (!q.trim()) return diff.changed;
    const k = q.trim().toLowerCase();
    return diff.changed.filter((x) => x.name.toLowerCase().includes(k));
  }, [diff, q]);

  return (
    <Card neon style={{ padding: 20 }}>
      <ScanLine />
      <CornerAccents />
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
        <div style={{ minWidth: 260, flex: 1 }}>
          <div style={{ color: "#9ca3af", fontWeight: "bold", marginBottom: 6 }}>Patch A</div>
          <Select value={aIdx} onChange={(e) => setAIdx(+e.target.value)} options={options} icon={<GitCompare size={16} />} />
        </div>
        <div style={{ minWidth: 260, flex: 1 }}>
          <div style={{ color: "#9ca3af", fontWeight: "bold", marginBottom: 6 }}>Patch B</div>
          <Select value={bIdx} onChange={(e) => setBIdx(+e.target.value)} options={options} icon={<GitCompare size={16} />} />
        </div>
        <div style={{ minWidth: 160 }}>
          <div style={{ color: "#9ca3af", fontWeight: "bold", marginBottom: 6 }}>Level</div>
          <Select
            value={level}
            onChange={(e) => setLevel(+e.target.value)}
            options={[...Array(15)].map((_, i) => ({ value: i + 1, label: `Lv ${i + 1}` }))}
          />
        </div>
        <div style={{ minWidth: 240, flex: 1 }}>
          <div style={{ color: "#9ca3af", fontWeight: "bold", marginBottom: 6 }}>Search hero</div>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="เช่น Airi" icon={<Users size={16} />} />
        </div>
      </div>

      {!canCompare ? (
        <div style={{ padding: 18, color: "#9ca3af" }}>
          ต้องมีอย่างน้อย 2 patch และเลือก A/B คนละอันก่อนนะครับ
        </div>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          <Card style={{ padding: 14 }}>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, fontWeight: "bold" }}>
                  <TrendingUp size={18} color="#22c55e" /> Added ({diff.added.length})
                </div>
                <div className="scroll-container" style={{ maxHeight: 160, overflow: "auto", color: "#9ca3af" }}>
                  {diff.added.slice(0, 200).map((n) => <div key={n}>+ {n}</div>)}
                  {diff.added.length > 200 && <div>... and more</div>}
                </div>
              </div>

              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, fontWeight: "bold" }}>
                  <TrendingDown size={18} color="#ef4444" /> Removed ({diff.removed.length})
                </div>
                <div className="scroll-container" style={{ maxHeight: 160, overflow: "auto", color: "#9ca3af" }}>
                  {diff.removed.slice(0, 200).map((n) => <div key={n}>- {n}</div>)}
                  {diff.removed.length > 200 && <div>... and more</div>}
                </div>
              </div>
            </div>
          </Card>

          <Card style={{ padding: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, fontWeight: "bold" }}>
              <GitCompare size={18} color="#ff1026" /> Changed ({filteredChanged.length})
            </div>

            <div className="scroll-container" style={{ maxHeight: 360, overflow: "auto", display: "grid", gap: 10 }}>
              {filteredChanged.slice(0, 200).map((c) => (
                <div key={c.name} style={{ padding: 12, borderRadius: 12, border: "1px solid rgba(255,16,38,0.25)", background: "rgba(0,0,0,0.55)" }}>
                  <div style={{ fontWeight: "bold", marginBottom: 8, color: "#fff" }}>{c.name}</div>
                  <div style={{ display: "grid", gap: 6, color: "#9ca3af" }}>
                    {c.diffs.map((d, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                        <span style={{ color: "#ffd7dc" }}>{d.f}</span>
                        <span style={{ fontFamily: "monospace" }}>
                          {String(d.a)} → {String(d.b)}{" "}
                          {typeof d.d === "number" && (
                            <span style={{ color: d.d > 0 ? "#22c55e" : "#ef4444" }}>
                              ({d.d > 0 ? "+" : ""}{d.d})
                            </span>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {filteredChanged.length > 200 && <div style={{ color: "#9ca3af" }}>... and more</div>}
            </div>
          </Card>
        </div>
      )}
    </Card>
  );
};

// ===================== Item Shop Picker (ROV Layout) =====================
const ItemShopPicker = ({ items, selected, setSelected, title = "Item Shop", compact = false }) => {
  const [cat, setCat] = useState("Attack");
  const [q, setQ] = useState("");
  const [focus, setFocus] = useState(null);
  const maxSlots = 6;

  const cats = ["Attack", "Magic", "Defense", "Movement", "Jungling", "Support"];
  const tiersRef = useRef(null);
  const [narrow, setNarrow] = useState(false);

  useEffect(() => {
    if (!tiersRef.current) return;
    const ro = new ResizeObserver(([entry]) => {
      const w = entry.contentRect.width;
      setNarrow(w < 900); // ปรับตัวเลขนี้ได้ตามที่คุณอยากให้สลับโหมด
    });
    ro.observe(tiersRef.current);
    return () => ro.disconnect();
  }, []);

  const filtered = useMemo(() => {
    const k = q.trim().toLowerCase();
    return (items || [])
      .filter((it) => (cat ? (it.category || "Unknown") === cat : true))
      .filter((it) => (!k ? true : (it.name || "").toLowerCase().includes(k)));
  }, [items, cat, q]);

  const byTier = useMemo(() => {
    const map = new Map();
    for (const it of filtered) {
      const t = Number.isFinite(+it.tier) ? +it.tier : 1;
      if (!map.has(t)) map.set(t, []);
      map.get(t).push(it);
    }
    const tiers = Array.from(map.keys()).sort((a, b) => a - b);
    return tiers.map((t) => ({
      tier: t,
      items: map.get(t).sort((a, b) => (+a.price || 0) - (+b.price || 0)),
    }));
  }, [filtered]);

  const totalPrice = selected.reduce((s, it) => s + (+it.price || 0), 0);

  const addToBuild = (it) => {
    if (!it) return;
    if (selected.length >= maxSlots) return;
    // ✅ อนุญาตให้ซ้ำได้
    setSelected((prev) => [...prev, it]);
  };


  const removeFromBuild = (idx) => setSelected((prev) => prev.filter((_, i) => i !== idx));
  const resetBuild = () => setSelected([]);

  const ItemThumb = ({ it, size = 44 }) => {
    const [idx, setIdx] = useState(0);
    const candidates = useMemo(() => getItemImageCandidates(it), [it]);
    const Icon = (ITEMS_CAT[it?.category]?.icon) || FileSpreadsheet;
    const src = candidates[idx] || null;

    return (
      <div
        style={{
          width: size,
          height: size,
          borderRadius: 10,
          overflow: "hidden",
          border: "1px solid rgba(255,16,38,0.25)",
          background: "rgba(0,0,0,0.6)",
          flex: "0 0 auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {src ? (
          <img
            src={src}
            alt={it?.name || ""}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            onError={() => setIdx((p) => p + 1)}
          />
        ) : (
          <Icon size={18} color="#ff1026" />
        )}
      </div>
    );
  };

  const focusStatsLines = useMemo(() => {
    const s = focus?.stats || {};
    const keys = getNumericStatKeys(s); // เอาเฉพาะตัวเลขที่ไม่ใช่ 0
    return keys.map((k) => ({
      key: k,
      label: STAT_LABELS[k] || k,
      value: +s[k] || 0,
    }));
  }, [focus]);

  return (
    <Card neon style={{ padding: 14 }}>
      <ScanLine />
      <CornerAccents />

      {/* TOP BAR */}
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
        <div style={{ fontWeight: "bold", display: "flex", alignItems: "center", gap: 10 }}>
          <FileSpreadsheet size={18} color="#ff1026" />
          {title}
        </div>

        <div style={{ marginLeft: "auto", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ color: "#ffd7dc", fontWeight: "bold" }}>
            Total: {totalPrice.toLocaleString()} gold
          </div>
          <Button variant="secondary" onClick={resetBuild} disabled={selected.length === 0}>
            Reset
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              const pool = filtered.slice().sort((a, b) => (+a.price || 0) - (+b.price || 0));
              const pick = pool
                .filter((x) => !selected.some((s) => s.name === x.name))
                .slice(0, maxSlots - selected.length);
              if (pick.length) setSelected((prev) => [...prev, ...pick]);
            }}
            disabled={selected.length >= maxSlots || filtered.length === 0}
          >
            Suggested
          </Button>
        </div>
      </div>

      {/* BUILD SLOTS */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
        {Array.from({ length: maxSlots }).map((_, i) => {
          const it = selected[i] || null;
          return (
            <div
              key={i}
              style={{
                width: 72,
                height: 72,
                borderRadius: 14,
                border: it ? "2px solid rgba(255,16,38,0.55)" : "2px solid rgba(255,16,38,0.2)",
                background: "rgba(0,0,0,0.55)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                cursor: it ? "pointer" : "default",
                boxShadow: it ? "0 0 18px rgba(255,16,38,0.25)" : "none",
              }}
              onClick={() => {
                if (!it) return;
                setFocus(it);
              }}
              title={it?.name || "Empty slot"}
            >
              {it ? (
                <>
                  <ItemThumb it={it} size={60} />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFromBuild(i);
                    }}
                    style={{
                      position: "absolute",
                      top: 6,
                      right: 6,
                      width: 22,
                      height: 22,
                      borderRadius: 999,
                      border: "none",
                      background: "rgba(0,0,0,0.6)",
                      color: "#ff1026",
                      cursor: "pointer",
                    }}
                    title="Remove"
                  >
                    <X size={14} />
                  </button>
                </>
              ) : (
                <div style={{ color: "#6b7280", fontWeight: "bold" }}>{i + 1}</div>
              )}
            </div>
          );
        })}
      </div>

      {/* MAIN LAYOUT */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: compact
            ? "minmax(200px, 240px) minmax(360px, 1fr)"
            : "minmax(200px, 240px) minmax(360px, 1fr) minmax(260px, 320px)",
          gap: 12,
          alignItems: "start",
          width: "100%",
          overflow: "hidden",
        }}
      >


        {/* LEFT: CATEGORY */}
        <Card style={{ padding: 12, minWidth: 0, overflow: "hidden" }}>
          <div style={{ fontWeight: "bold", color: "#ffd7dc", marginBottom: 10 }}>Category</div>
          <div style={{ display: "grid", gap: 8 }}>
            {cats.map((c) => {
              const active = c === cat;
              return (
                <button
                  key={c}
                  onClick={() => {
                    setCat(c);
                    setFocus(null);
                  }}
                  style={{
                    textAlign: "left",
                    padding: "10px 12px",
                    borderRadius: 12,
                    border: active ? "2px solid rgba(255,16,38,0.6)" : "2px solid rgba(255,16,38,0.2)",
                    background: active
                      ? "linear-gradient(135deg, rgba(255,16,38,0.25), rgba(0,0,0,0.55))"
                      : "rgba(0,0,0,0.55)",
                    color: active ? "#fff" : "#9ca3af",
                    cursor: "pointer",
                    fontFamily: "'Rajdhani', sans-serif",
                    fontWeight: "bold",
                  }}
                >
                  {c}
                </button>
              );
            })}
          </div>

          <div style={{ marginTop: 12 }}>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search item..." icon={<Target size={16} />} />
          </div>
        </Card>

        {/* CENTER: ITEM LIST BY TIER (columns) */}
        <Card style={{ padding: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={{ fontWeight: "bold", color: "#ffd7dc" }}>{cat}</div>
            <div style={{ color: "#9ca3af" }}>{filtered.length.toLocaleString()} items</div>
          </div>
          <div
            ref={tiersRef}
            className="scroll-container"
            style={{
              display: "grid",
              gridTemplateColumns: (compact || narrow)
                ? "1fr" // ✅ พื้นที่แคบ: เรียง Tier ลงล่าง เห็นครบทุก Tier ไม่ต้องเลื่อนซ้ายขวา
                : `repeat(${Math.max(1, byTier.length)}, minmax(260px, 1fr))`,
              gap: 10,
              maxHeight: 520,
              overflowY: "auto",
              overflowX: "hidden",
              paddingRight: 6,
            }}
          >

            {byTier.map((group) => (
              <div key={group.tier} style={{ borderRadius: 12, border: "1px solid rgba(255,16,38,0.2)", overflow: "hidden" }}>
                <div
                  style={{
                    padding: "10px 12px",
                    background: "linear-gradient(to right, rgba(255,16,38,0.18), rgba(0,0,0,0.85))",
                    borderBottom: "1px solid rgba(255,16,38,0.2)",
                    fontWeight: "bold",
                    color: "#ffd7dc",
                    letterSpacing: "0.08em",
                  }}
                >
                  Tier {group.tier}
                </div>

                {group.items.map((it) => {
                  const active = focus?.name === it.name;
                  return (
                    <button
                      key={it.name}
                      type="button"
                      onClick={() => setFocus(it)}
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "10px 12px",
                        border: "none",
                        borderTop: "1px solid rgba(255,16,38,0.12)",
                        background: active ? "rgba(255,16,38,0.15)" : "rgba(0,0,0,0.0)",
                        cursor: "pointer",
                        color: "#fff",
                        textAlign: "left",
                        fontFamily: "'Rajdhani', sans-serif",
                      }}
                    >
                      <ItemThumb it={it} size={40} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: "bold" }}>{it.name}</div>
                        <div style={{ color: "#9ca3af", fontSize: 13 }}>
                          {(+it.price || 0).toLocaleString()} gold
                        </div>
                      </div>

                      <Button
                        variant="secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          addToBuild(it);
                        }}
                        disabled={selected.length >= maxSlots}
                        style={{ padding: "10px 14px" }}
                      >
                        Add
                      </Button>
                    </button>
                  );
                })}

                {group.items.length === 0 && <div style={{ padding: 12, color: "#9ca3af" }}>No items</div>}
              </div>
            ))}

            {byTier.length === 0 && <div style={{ padding: 12, color: "#9ca3af" }}>No item found</div>}
          </div>
        </Card>

        {/* RIGHT: DETAIL */}
        <Card style={{ padding: 12, minWidth: 0, overflow: "hidden" }}>
          <div style={{ fontWeight: "bold", color: "#ffd7dc", marginBottom: 10 }}>Details</div>

          {!focus ? (
            <div style={{ color: "#9ca3af" }}>คลิกไอเท็มตรงกลางเพื่อดูรายละเอียด</div>
          ) : (
            <>
              <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 10 }}>
                <ItemThumb it={focus} size={64} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: 18 }}>
                    {focus.name}
                  </div>
                  <div style={{ color: "#9ca3af" }}>
                    {focus.category || "Unknown"} • Tier {focus.tier || 1} • {(+focus.price || 0).toLocaleString()} gold
                  </div>
                </div>
              </div>

              <div style={{ display: "grid", gap: 8, marginBottom: 10 }}>
                {focusStatsLines.length === 0 ? (
                  <div style={{ color: "#9ca3af" }}>ไม่มีสเตตัสในข้อมูล</div>
                ) : (
                  focusStatsLines.map((x) => (
                    <div
                      key={x.key}
                      style={{ display: "flex", justifyContent: "space-between", color: "#9ca3af" }}
                    >
                      <span style={{ color: "#ffd7dc", fontWeight: "bold" }}>{x.label}</span>
                      <span style={{ fontFamily: "monospace", fontWeight: "bold", color: "#fff" }}>
                        {x.value > 0 ? "+" : ""}{Math.round(x.value).toLocaleString()}
                      </span>
                    </div>
                  ))
                )}
              </div>


              <div style={{ borderTop: "1px solid rgba(255,16,38,0.2)", paddingTop: 10 }}>
                <div style={{ color: "#ffd7dc", fontWeight: "bold", marginBottom: 6 }}>Passive</div>
                <div style={{ color: "#9ca3af", whiteSpace: "pre-wrap", lineHeight: 1.35 }}>
                  {focus.passive || "—"}
                </div>
              </div>

              <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
                <Button
                  onClick={() => addToBuild(focus)}
                  disabled={selected.length >= maxSlots}
                >
                  Equip
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    alert("Item Tree: ถ้าคุณมีสูตรใน Excel (builds_from / upgrades_to) ผมต่อเส้น tree ให้ได้ครับ");
                  }}
                >
                  Item Tree
                </Button>
              </div>
            </>
          )}
        </Card>
      </div>
    </Card>
  );
};

// ===================== HeroComboBox (dropdown with images) =====================
const HeroComboBox = ({ heroes, value, onChange, label, placeholder = "Select hero..." }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  const selectedHero = useMemo(() => heroes.find((h) => h.name === value) || null, [heroes, value]);

  const filtered = useMemo(() => {
    const k = q.trim().toLowerCase();
    if (!k) return heroes;
    return heroes.filter((h) => h.name.toLowerCase().includes(k));
  }, [heroes, q]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const pick = (name) => {
    onChange({ target: { value: name } });
    setOpen(false);
    setQ("");
  };

  const selectedImg = selectedHero ? getHeroImageUrl(selectedHero.name) : null;

  return (
    <div style={{ minWidth: 260, flex: 1 }} ref={ref}>
      <div style={{ color: "#9ca3af", fontWeight: "bold", marginBottom: 6 }}>{label}</div>

      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 12px",
          background: "rgba(0, 0, 0, 0.6)",
          border: "2px solid rgba(255,16,38,0.3)",
          borderRadius: "12px",
          color: "#ffffff",
          cursor: "pointer",
          transition: "all 0.3s",
          fontFamily: "'Rajdhani', sans-serif",
          outline: "none",
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 8,
            overflow: "hidden",
            border: "1px solid rgba(255,16,38,0.25)",
            background: "rgba(0,0,0,0.6)",
            flex: "0 0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {selectedImg ? (
            <img src={selectedImg} alt={selectedHero?.name || ""} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <Users size={16} color="#ff1026" />
          )}
        </div>

        <div style={{ flex: 1, textAlign: "left", fontWeight: "bold" }}>
          {selectedHero?.name || <span style={{ color: "#9ca3af" }}>{placeholder}</span>}
        </div>

        <div style={{ color: "#ff1026", display: "flex", alignItems: "center" }}>
          <Target size={16} />
        </div>
      </button>

      {open && (
        <div style={{ position: "relative", marginTop: 8 }}>
          <div
            style={{
              position: "absolute",
              zIndex: 60,
              left: 0,
              right: 0,
              background: "rgba(0,0,0,0.92)",
              border: "2px solid rgba(255,16,38,0.35)",
              borderRadius: 12,
              boxShadow: "0 0 30px rgba(255,16,38,0.2)",
              overflow: "hidden",
            }}
          >
            <div style={{ padding: 10 }}>
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search hero..." icon={<Users size={16} />} />
            </div>

            <div className="scroll-container" style={{ maxHeight: 320, overflow: "auto" }}>
              {filtered.slice(0, 400).map((h) => {
                const img = getHeroImageUrl(h.name);
                const RoleIcon = ROLES[h.role]?.icon || Users;
                return (
                  <button
                    key={h.name}
                    type="button"
                    onClick={() => pick(h.name)}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "10px 12px",
                      background: h.name === value ? "rgba(255,16,38,0.15)" : "rgba(0,0,0,0.0)",
                      border: "none",
                      borderTop: "1px solid rgba(255,16,38,0.12)",
                      cursor: "pointer",
                      color: "#fff",
                      textAlign: "left",
                      fontFamily: "'Rajdhani', sans-serif",
                    }}
                  >
                    <div
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: 10,
                        overflow: "hidden",
                        border: "1px solid rgba(255,16,38,0.25)",
                        background: "rgba(0,0,0,0.6)",
                        flex: "0 0 auto",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {img ? (
                        <img src={img} alt={h.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      ) : (
                        <Users size={18} color="#ff1026" />
                      )}
                    </div>

                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: "bold" }}>{h.name}</div>
                      <div style={{ color: "#9ca3af", fontSize: 13, display: "flex", gap: 8, alignItems: "center" }}>
                        <RoleIcon size={14} color={ROLES[h.role]?.color || "#ff1026"} />
                        {h.role || "-"} • {h.lane || "-"} {LANES[h.lane] || ""}
                      </div>
                    </div>
                  </button>
                );
              })}

              {filtered.length === 0 && <div style={{ padding: 12, color: "#9ca3af" }}>No hero found</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ===================== Hero selector =====================
const HeroSelect = ({ heroes, value, onChange, label }) => (
  <HeroComboBox heroes={heroes} value={value} onChange={onChange} label={label} />
);

const DEFAULT_LEVELS = [...Array(15)].map((_, i) => i + 1);

const getHeroLevels = (hero) => {
  const levels = Object.keys(hero?.levels || {})
    .map((x) => parseInt(x, 10))
    .filter((n) => Number.isFinite(n))
    .sort((a, b) => a - b);
  return levels.length ? levels : DEFAULT_LEVELS;
};

const LevelSelect = ({ value, onChange, label = "Level", levels }) => (
  <div style={{ minWidth: 150 }}>
    <div style={{ color: "#9ca3af", fontWeight: "bold", marginBottom: 6 }}>{label}</div>
    <Select
      value={value}
      onChange={onChange}
      options={(levels?.length ? levels : DEFAULT_LEVELS).map((lv) => ({ value: lv, label: `Lv ${lv}` }))}
    />
  </div>
);

const getHeroLevelObj = (hero, level) => hero?.levels?.[level] || hero?.levels?.[1] || Object.values(hero?.levels || {})[0] || null;

const ArcanaSelector = ({ arcanas, selected, onChange }) => {
  // arcanas คือ list ของรูนทั้งหมดจาก parse
  // selected คือ { red: obj, purple: obj, green: obj }
  // onChange ส่งค่ากลับไป update state

  // เป็น:
  const filterArcana = (color) => arcanas.filter(a =>
    a.type === "ARCANA" &&
    String(a.color).toLowerCase() === color.toLowerCase() &&
    a.tier === 3
  );
  const SelectBox = ({ color, label, icon: Icon, colorCode }) => {
    const list = useMemo(() => filterArcana(color), [arcanas, color]);
    const current = selected[color.toLowerCase()];

    return (
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ color: colorCode, fontWeight: "bold", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
          {Icon && <Icon size={14} />} {label} (x10)
        </div>
        <select
          value={current?.name || ""}
          onChange={(e) => {
            const val = list.find(x => x.name === e.target.value);
            onChange(color.toLowerCase(), val);
          }}
          style={{
            width: "100%",
            padding: "8px 10px",
            background: "rgba(0,0,0,0.6)",
            border: `1px solid ${colorCode}`,
            borderRadius: 8,
            color: "#fff",
            fontFamily: "'Rajdhani', sans-serif"
          }}
        >
          <option value="">- Empty -</option>
          {list.map(a => (
            <option key={a.name} value={a.name}>
              {a.name} ({Object.keys(a.stats).map(k => `${STAT_LABELS[k] || k} ${a.stats[k]}`).join(", ")})
            </option>
          ))}
        </select>
      </div>
    );
  };

  return (
    <Card style={{ padding: 14, marginBottom: 14, border: "1px solid rgba(255,255,255,0.1)" }}>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <SelectBox color="Red" label="Red Arcana (ATK)" icon={Sword} colorCode="#ef4444" />
        <SelectBox color="Purple" label="Purple Arcana (HP/Sustain)" icon={Zap} colorCode="#a855f7" />
        <SelectBox color="Green" label="Green Arcana (DEF)" icon={Shield} colorCode="#22c55e" />
      </div>
    </Card>
  );
};// ===================== Overall Stats Tab =====================
const OverallStatTab = ({ patch }) => {
  const heroes = useMemo(() => [...patch.heroes].sort((a, b) => a.name.localeCompare(b.name)), [patch]);
  const items = patch.items || [];

  const [heroName, setHeroName] = useState(heroes[0]?.name || "");
  const [level, setLevel] = useState(1);
  const [selectedItems, setSelectedItems] = useState([]);
  const [arcanaSet, setArcanaSet] = useState({ red: null, purple: null, green: null });

  const hero = useMemo(() => heroes.find((h) => h.name === heroName), [heroes, heroName]);
  const levels = useMemo(() => getHeroLevels(hero), [hero]);

  useEffect(() => {
    if (levels.length && !levels.includes(level)) {
      setLevel(levels[levels.length - 1] || 1);
    }
  }, [levels, level]);

  const heroLv = useMemo(() => getHeroLevelObj(hero, level), [hero, level]);

  const stats = useMemo(() => computeTotalStatsRealistic(heroLv, selectedItems, arcanaSet), [heroLv, selectedItems, arcanaSet]); const statKeys = useMemo(() => getAllStatKeys(stats.total), [stats]);

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <ArcanaSelector
        arcanas={patch.runes || []} // ใน parse เรา push ลง d.runes
        selected={arcanaSet}
        onChange={(color, val) => setArcanaSet(prev => ({ ...prev, [color]: val }))}
      />
      {/* Hero Selection Card */}
      <Card neon style={{ padding: 20 }}>
        <ScanLine />
        <CornerAccents />
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
          <HeroSelector
            heroes={heroes}
            value={heroName}
            onChange={(e) => setHeroName(e.target.value)}
            label="Hero"
            getHeroImageUrl={getHeroImageUrl}
            showRoleFilter={true}
            maxHeight={400}
          />
          <LevelSelect value={level} onChange={(e) => setLevel(+e.target.value)} levels={levels} />
          <div style={{ minWidth: 200 }}>
            <div style={{ color: "#9ca3af", fontWeight: "bold", marginBottom: 6 }}>Role / Lane</div>
            <div style={{ padding: "10px 12px", borderRadius: 12, border: "2px solid rgba(255,16,38,0.25)", background: "rgba(0,0,0,0.55)" }}>
              <span style={{ fontWeight: "bold" }}>{hero?.role || "-"}</span>{" "}
              <span style={{ color: "#9ca3af" }}>
                • {hero?.lane || "-"} {LANES[hero?.lane] || ""}
              </span>
            </div>
          </div>
        </div>

        {/* Stats Grid + Radar Chart */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 14, alignItems: "start" }}>
          {/* LEFT: Stats Grid */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12 }}>
            {statKeys.map((k) => {
              const v = stats.total[k] || 0;
              const b = stats.base[k] || 0;
              const flatAdd = stats.flat?.[k] || 0;
              const pctAdd = stats.pct?.[k] || 0;
              const pctOfBaseKey = k + '_pct';
              const pctOfBase = stats.pctOfBase?.[pctOfBaseKey] || 0;

              return (
                <Card key={k} style={{ padding: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                    <div style={{ fontWeight: "bold" }}>{STAT_LABELS_NEW[k] || k}</div>
                    <div style={{ fontFamily: "monospace", fontWeight: "bold" }}>
                      {formatStatValue(k, v)}
                    </div>
                  </div>

                  {/* แสดง breakdown */}
                  <div style={{ marginTop: 8, color: "#9ca3af", fontSize: 12 }}>
                    <div>Base: {Math.round(b).toLocaleString()}</div>
                    {flatAdd !== 0 && (
                      <div style={{ color: "#22c55e" }}>
                        + Flat: {flatAdd > 0 ? '+' : ''}{Math.round(flatAdd).toLocaleString()}
                      </div>
                    )}
                    {pctAdd !== 0 && (
                      <div style={{ color: "#3b82f6" }}>
                        + Item: {pctAdd > 0 ? '+' : ''}{pctAdd}%
                      </div>
                    )}
                    {pctOfBase !== 0 && (
                      <div style={{ color: "#f59e0b" }}>
                        × Bonus: {pctOfBase}%
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>

          {/* RIGHT: Radar Chart */}
          <Card style={{
            padding: 20,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            position: "sticky",
            top: 120
          }}>
            <div style={{
              fontWeight: "bold",
              color: "#ffd7dc",
              marginBottom: 12,
              fontFamily: "'Orbitron', sans-serif",
              letterSpacing: "0.1em"
            }}>
              STAT RADAR
            </div>
            <RadarChart stats={stats.total} size={260} label1={heroName} />
            <div style={{
              marginTop: 12,
              color: "#9ca3af",
              fontSize: 12,
              textAlign: "center"
            }}>
              {heroName} Lv.{level}
            </div>
          </Card>
        </div>
      </Card>

      {/* Item Shop */}
      <ItemShopPicker
        items={items}
        selected={selectedItems}
        setSelected={setSelectedItems}
        title="Build Items"
      />
    </div>
  );
};

// ===================== Hero Gallery Tab =====================
const HeroGalleryTab = ({ patch }) => {
  const heroes = useMemo(() => [...patch.heroes].sort((a, b) => a.name.localeCompare(b.name)), [patch]);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("ALL");
  const [lane, setLane] = useState("ALL");
  const [level, setLevel] = useState(1);
  const [selected, setSelected] = useState(null);

  const roles = useMemo(() => ["ALL", ...Array.from(new Set(heroes.map((h) => h.role || "Unknown"))).sort()], [heroes]);
  const lanes = useMemo(() => ["ALL", ...Array.from(new Set(heroes.map((h) => h.lane || "Unknown"))).sort()], [heroes]);

  const filtered = useMemo(() => {
    const k = q.trim().toLowerCase();
    return heroes
      .filter((h) => (role === "ALL" ? true : (h.role || "Unknown") === role))
      .filter((h) => (lane === "ALL" ? true : (h.lane || "Unknown") === lane))
      .filter((h) => (!k ? true : h.name.toLowerCase().includes(k)));
  }, [heroes, q, role, lane]);

  const detailHero = selected ? heroes.find((h) => h.name === selected) : null;
  const levelHero = detailHero || heroes[0] || null;
  const levels = useMemo(() => getHeroLevels(levelHero), [levelHero]);

  useEffect(() => {
    if (levels.length && !levels.includes(level)) {
      setLevel(levels[levels.length - 1] || 1);
    }
  }, [levels, level]);

  const detailLv = useMemo(() => getHeroLevelObj(detailHero, level), [detailHero, level]);
  const detailKeys = useMemo(() => getAllStatKeys(detailLv), [detailLv]);

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <Card neon style={{ padding: 18 }}>
        <ScanLine />
        <CornerAccents />
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search hero..." icon={<Users size={16} />} />
          </div>
          <div style={{ width: 220 }}>
            <Select value={role} onChange={(e) => setRole(e.target.value)} options={roles.map((r) => ({ value: r, label: r }))} />
          </div>
          <div style={{ width: 220 }}>
            <Select value={lane} onChange={(e) => setLane(e.target.value)} options={lanes.map((l) => ({ value: l, label: l }))} />
          </div>
          <div style={{ width: 160 }}>
            <Select
              value={level}
              onChange={(e) => setLevel(+e.target.value)}
              options={levels.map((lv) => ({ value: lv, label: `Lv ${lv}` }))}
            />
          </div>
        </div>
      </Card>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
        {filtered.slice(0, 400).map((h) => {
          const RoleIcon = (ROLES[h.role]?.icon) || Users;
          const img = getHeroImageUrl(h.name);
          return (
            <Card
              key={h.name}
              style={{
                padding: 12,
                cursor: "pointer",
                border: selected === h.name ? "2px solid rgba(255,16,38,0.7)" : "1px solid rgba(255,16,38,0.2)",
                boxShadow: selected === h.name ? "0 0 22px rgba(255,16,38,0.25)" : "none",
              }}
              onClick={() => setSelected(h.name)}
            >
              <div style={{ display: "flex", gap: 12 }}>
                <div style={{ width: 64, height: 64, borderRadius: 14, overflow: "hidden", border: "1px solid rgba(255,16,38,0.25)", background: "rgba(0,0,0,0.6)" }}>
                  {img ? (
                    <img src={img} alt={h.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#ff1026" }}>
                      <Users size={22} />
                    </div>
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: "bold", fontSize: 16 }}>{h.name}</div>
                  <div style={{ color: "#9ca3af", fontSize: 13, display: "flex", alignItems: "center", gap: 8, marginTop: 2 }}>
                    <RoleIcon size={14} color={ROLES[h.role]?.color || "#ff1026"} />
                    {h.role || "-"} • {h.lane || "-"} {LANES[h.lane] || ""}
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {detailHero && (
        <Card neon style={{ padding: 18 }}>
          <ScanLine />
          <CornerAccents />
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
            <div>
              <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: 22 }}>{detailHero.name}</div>
              <div style={{ color: "#9ca3af" }}>
                {detailHero.role || "-"} • {detailHero.lane || "-"} {LANES[detailHero.lane] || ""}
              </div>
            </div>
            <div style={{ color: "#9ca3af" }}>Stats at Lv {level}</div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
            {detailKeys.map((k) => {
              const v = detailLv?.[k] || 0;

              return (
                <Card key={k} style={{ padding: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                    <div style={{ fontWeight: "bold" }}>{STAT_LABELS[k] || k}</div>
                    <div style={{ fontFamily: "monospace", fontWeight: "bold" }}>{Math.round(v).toLocaleString()}</div>
                  </div>
                </Card>
              );
            })}

          </div>
        </Card>
      )}
    </div>
  );
};

// ===================== Items Tab =====================
const ItemsTab = ({ patch }) => {
  const items = patch.items || [];
  const [cart, setCart] = useState([]);
  const totalPrice = cart.reduce((s, it) => s + (+it.price || 0), 0);

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <ItemShopPicker items={items} selected={cart} setSelected={setCart} title="Item Shop + Price Calculator" />
      <Card style={{ padding: 16 }}>
        <div style={{ fontWeight: "bold", color: "#ffd7dc" }}>รวมราคา Build: {totalPrice.toLocaleString()} gold</div>
        <div style={{ color: "#9ca3af", marginTop: 6 }}>(เลือกได้สูงสุด 6 ชิ้น — ใช้ตะกร้าด้านบน)</div>
      </Card>
    </div>
  );
};

// ===================== Damage Calculator Tab =====================
const DamageCalculatorTab = ({ patch }) => {
  const heroes = useMemo(() => [...patch.heroes].sort((a, b) => a.name.localeCompare(b.name)), [patch]);
  const items = patch.items || [];
  const skills = patch.skills || [];

  const [atkHeroName, setAtkHeroName] = useState(heroes[0]?.name || "");
  const [defHeroName, setDefHeroName] = useState(heroes[1]?.name || heroes[0]?.name || "");
  const [atkLv, setAtkLv] = useState(15);
  const [defLv, setDefLv] = useState(15);

  const [atkItems, setAtkItems] = useState([]);
  const [defItems, setDefItems] = useState([]);

  const [mode, setMode] = useState("SKILL");
  const [dmgType, setDmgType] = useState("PHYS");
  const [manualBase, setManualBase] = useState("500");

  const [skillName, setSkillName] = useState("");
  const [skillRank, setSkillRank] = useState(1);

  const atkHero = useMemo(() => heroes.find((h) => h.name === atkHeroName), [heroes, atkHeroName]);
  const defHero = useMemo(() => heroes.find((h) => h.name === defHeroName), [heroes, defHeroName]);

  const atkHeroLv = useMemo(() => getHeroLevelObj(atkHero, atkLv), [atkHero, atkLv]);
  const defHeroLv = useMemo(() => getHeroLevelObj(defHero, defLv), [defHero, defLv]);

  const atkStats = useMemo(() => computeTotalStatsRealistic(atkHeroLv, atkItems).total, [atkHeroLv, atkItems]);
  const defStats = useMemo(() => computeTotalStatsRealistic(defHeroLv, defItems).total, [defHeroLv, defItems]);

  const atkSkills = useMemo(() => skills.filter((s) => s.hero === atkHeroName), [skills, atkHeroName]);
  useEffect(() => {
    if (!skillName && atkSkills.length) setSkillName(atkSkills[0].name);
  }, [atkSkills, skillName]);

  const skill = useMemo(() => atkSkills.find((s) => s.name === skillName), [atkSkills, skillName]);

  const raw = useMemo(() => {
    if (mode === "MANUAL") return +manualBase || 0;
    if (!skill) return 0;
    const base = skill.base_dmg?.[Math.max(0, Math.min(5, skillRank - 1))] || 0;
    return computeSkillRawDamage({ base, adScaling: skill.ad_scaling, apScaling: skill.ap_scaling, attackerStats: atkStats });
  }, [mode, manualBase, skill, skillRank, atkStats]);
  // [NEW] ตรวจสอบ Fenrir
  const hasFenrir = atkItems.some(i => i.name.toLowerCase().includes("fenrir"));
  const fenrirBonus = hasFenrir ? 1.3 : 1.0; // สมมติว่า Active ตลอด (หรือจะทำ Checkbox "Target HP < 50%" ก็ได้)

  const final = useMemo(() => {
    let dmg = 0;
    if (dmgType === "PHYS") {
      dmg = applyDefenseWithPenetration(raw, defStats.phys_def, atkStats.armor_pierce, atkStats.armor_pierce_pct);
    } else {
      dmg = applyDefenseWithPenetration(raw, defStats.magic_def, atkStats.magic_pierce, atkStats.magic_pierce_pct);
    }

    // Apply Fenrir Multiplier
    return dmg * fenrirBonus;

  }, [raw, dmgType, defStats, atkStats, fenrirBonus]);

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <Card neon style={{ padding: 18 }}>
        <ScanLine />
        <CornerAccents />
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <HeroSelector heroes={heroes} value={atkHeroName} onChange={(e) => setAtkHeroName(e.target.value)} label="Attacker" getHeroImageUrl={getHeroImageUrl}
            showRoleFilter={true}
            maxHeight={400} />
          <LevelSelect value={atkLv} onChange={(e) => setAtkLv(+e.target.value)} label="Atk Lv" />
          <HeroSelector heroes={heroes} value={defHeroName} onChange={(e) => setDefHeroName(e.target.value)} label="Defender" getHeroImageUrl={getHeroImageUrl}
            showRoleFilter={true}
            maxHeight={400} />
          <LevelSelect value={defLv} onChange={(e) => setDefLv(+e.target.value)} label="Def Lv" />
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
          <div style={{ width: 200 }}>
            <div style={{ color: "#9ca3af", fontWeight: "bold", marginBottom: 6 }}>Mode</div>
            <Select
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              options={[
                { value: "SKILL", label: "Use Skill (from Excel)" },
                { value: "MANUAL", label: "Manual Base Damage" },
              ]}
              icon={<Calculator size={16} />}
            />
          </div>

          <div style={{ width: 180 }}>
            <div style={{ color: "#9ca3af", fontWeight: "bold", marginBottom: 6 }}>Damage Type</div>
            <Select
              value={dmgType}
              onChange={(e) => setDmgType(e.target.value)}
              options={[
                { value: "PHYS", label: "Physical" },
                { value: "MAGIC", label: "Magic" },
              ]}
            />
          </div>

          {mode === "MANUAL" ? (
            <div style={{ minWidth: 220, flex: 1 }}>
              <div style={{ color: "#9ca3af", fontWeight: "bold", marginBottom: 6 }}>Base Damage</div>
              <Input value={manualBase} onChange={(e) => setManualBase(e.target.value)} placeholder="เช่น 500" icon={<Sword size={16} />} />
            </div>
          ) : (
            <>
              <div style={{ minWidth: 280, flex: 1 }}>
                <div style={{ color: "#9ca3af", fontWeight: "bold", marginBottom: 6 }}>Skill</div>
                <Select value={skillName} onChange={(e) => setSkillName(e.target.value)} options={atkSkills.map((s) => ({ value: s.name, label: s.name }))} icon={<Zap size={16} />} />
              </div>
              <div style={{ width: 160 }}>
                <div style={{ color: "#9ca3af", fontWeight: "bold", marginBottom: 6 }}>Skill Rank</div>
                <Select value={skillRank} onChange={(e) => setSkillRank(+e.target.value)} options={[1, 2, 3, 4, 5, 6].map((x) => ({ value: x, label: `Rank ${x}` }))} />
              </div>
            </>
          )}
        </div>

        <Card style={{ padding: 14, marginTop: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
            <div style={{ color: "#9ca3af" }}>
              Raw Damage: <span style={{ color: "#fff", fontFamily: "monospace", fontWeight: "bold" }}>{Math.round(raw).toLocaleString()}</span>
            </div>
            <div style={{ color: "#9ca3af" }}>
              Final Damage (after DEF):{" "}
              <span style={{ color: "#ffd7dc", fontFamily: "monospace", fontWeight: "bold" }}>{Math.round(final).toLocaleString()}</span>
            </div>
          </div>
          {mode === "SKILL" && skill && (
            <div style={{ marginTop: 8, color: "#9ca3af", fontSize: 13 }}>
              Scaling: AD {skill.ad_scaling} • AP {skill.ap_scaling} • Base[{skillRank}] = {(skill.base_dmg?.[skillRank - 1] || 0).toLocaleString()}
            </div>
          )}
        </Card>
      </Card>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(520px, 1fr))", gap: 14 }}>
        <ItemShopPicker items={items} selected={atkItems} setSelected={setAtkItems} title="Attacker Items" />
        <ItemShopPicker items={items} selected={defItems} setSelected={setDefItems} title="Defender Items" />
      </div>
    </div>
  );
};

// ===================== Level Progression Tab =====================
const LevelProgressionTab = ({ patch }) => {
  const heroes = useMemo(() => [...patch.heroes].sort((a, b) => a.name.localeCompare(b.name)), [patch]);
  const [heroName, setHeroName] = useState(heroes[0]?.name || "");
  // เพิ่ม State สำหรับเก็บเลเวลที่เลือก
  const [selectedLevel, setSelectedLevel] = useState(1);

  const hero = useMemo(() => heroes.find((h) => h.name === heroName), [heroes, heroName]);

  // ดึงรายการเลเวลทั้งหมดที่มีในไฟล์ Excel ของฮีโร่ตัวนั้น
  const levels = useMemo(() => {
    const arr = Object.keys(hero?.levels || {}).map((x) => +x).sort((a, b) => a - b);
    return arr.length ? arr : [1];
  }, [hero]);

  // ดึงข้อมูล Stats ของเลเวลที่เลือก
  const selectedLevelData = useMemo(() => {
    return getHeroLevelObj(hero, selectedLevel);
  }, [hero, selectedLevel]);

  const statKeys = useMemo(() => {
    return getAllStatKeys(selectedLevelData);
  }, [selectedLevelData]);

  return (
    <Card neon style={{ padding: 18 }}>
      <ScanLine />
      <CornerAccents />

      {/* ส่วนหัว: เลือกฮีโร่ และ เลือกเลเวล (Dropdown) */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", marginBottom: 20 }}>
        <HeroSelector
          heroes={heroes}
          value={heroName}
          onChange={(e) => {
            setHeroName(e.target.value);
            setSelectedLevel(1); // รีเซ็ตเป็นเลเวล 1 เมื่อเปลี่ยนตัวละคร
          }}
          label="Hero"
          getHeroImageUrl={getHeroImageUrl}
          showRoleFilter={true}
          maxHeight={400}
        />

        <div style={{ minWidth: 200 }}>
          <div style={{ color: "#9ca3af", fontWeight: "bold", marginBottom: 6 }}>Select Level</div>
          <Select
            value={selectedLevel}
            onChange={(e) => setSelectedLevel(+e.target.value)}
            options={levels.map(lv => ({ value: lv, label: `Level ${lv}` }))}
            icon={<TrendingUp size={16} />}
          />
        </div>

        <div style={{ minWidth: 220 }}>
          <div style={{ color: "#9ca3af", fontWeight: "bold", marginBottom: 6 }}>Hero Info</div>
          <div style={{ padding: "10px 12px", borderRadius: 12, border: "2px solid rgba(255,16,38,0.25)", background: "rgba(0,0,0,0.55)", color: "#fff" }}>
            {hero?.role || "-"} • {hero?.lane || "-"} {LANES[hero?.lane] || ""}
          </div>
        </div>
      </div>

      {/* ส่วนแสดงผลสเตตัสแบบเน้นเลเวลที่เลือก */}
      <div className="scroll-container" style={{ maxHeight: 550, overflow: "auto", borderRadius: 16, border: "1px solid rgba(255,16,38,0.2)", background: "rgba(0,0,0,0.3)" }}>
        {selectedLevelData ? (
          <div style={{ padding: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, borderBottom: "1px solid rgba(255,16,38,0.3)", paddingBottom: 10 }}>
              <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: "1.5rem", fontWeight: 900, color: "#ff1026" }}>
                LEVEL {selectedLevel} <span style={{ fontSize: "1rem", color: "#9ca3af", marginLeft: 10 }}>BASE STATISTICS</span>
              </div>
            </div>

            {/* แสดงสเตตัสแบบ Grid 2 คอลัมน์เพื่อความอ่านง่าย */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "12px 24px" }}>
              {statKeys.map((k) => {
                const val = selectedLevelData[k] || 0;
                return (
                  <div
                    key={k}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "8px 12px",
                      background: "rgba(255,16,38,0.05)",
                      borderRadius: "8px",
                      border: "1px solid rgba(255,16,38,0.1)"
                    }}
                  >
                    <span style={{ color: "#ffd7dc", fontWeight: "600", fontSize: "0.95rem" }}>
                      {STAT_LABELS[k] || k}
                    </span>
                    <span style={{ fontFamily: "monospace", fontWeight: "bold", color: "#fff", fontSize: "1.1rem" }}>
                      {Math.round(val).toLocaleString()}
                      {/* ถ้าเป็นสเตตัสที่เป็น % ให้เติมเครื่องหมายหลังตัวเลข */}
                      {k.includes('pct') || k.includes('rate') || k.includes('speed') || k.includes('reduction') ? '%' : ''}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div style={{ padding: 40, textAlign: "center", color: "#6b7280" }}>
            <AlertCircle size={48} style={{ marginBottom: 10, opacity: 0.5 }} />
            <p>No data found for Level {selectedLevel}</p>
          </div>
        )}
      </div>
    </Card>
  );
};

// ===================== 1v1 Simulator Tab =====================
const Simulator1v1Tab = ({ patch }) => {
  const heroes = useMemo(() => [...patch.heroes].sort((a, b) => a.name.localeCompare(b.name)), [patch]);
  const items = patch.items || [];

  const [aHeroName, setAHeroName] = useState(heroes[0]?.name || "");
  const [bHeroName, setBHeroName] = useState(heroes[1]?.name || heroes[0]?.name || "");
  const [aLv, setALv] = useState(15);
  const [bLv, setBLv] = useState(15);
  const [aItems, setAItems] = useState([]);
  const [bItems, setBItems] = useState([]);
  const [aType, setAType] = useState("PHYS"); // auto-attack type
  const [atkSpeed, setAtkSpeed] = useState("1.0"); // hits/sec (simple)

  const heroA = useMemo(() => heroes.find((h) => h.name === aHeroName), [heroes, aHeroName]);
  const heroB = useMemo(() => heroes.find((h) => h.name === bHeroName), [heroes, bHeroName]);

  const aObj = useMemo(() => getHeroLevelObj(heroA, aLv), [heroA, aLv]);
  const bObj = useMemo(() => getHeroLevelObj(heroB, bLv), [heroB, bLv]);

  const aStats = useMemo(() => computeTotalStatsRealistic(aObj, aItems).total, [aObj, aItems]);
  const bStats = useMemo(() => computeTotalStatsRealistic(bObj, bItems).total, [bObj, bItems]);

  // ✅ ใหม่
  const aPerHit = useMemo(() => {
    const raw = aType === "PHYS" ? aStats.phys_atk : aStats.magic_power;
    const def = aType === "PHYS" ? bStats.phys_def : bStats.magic_def;
    const flatPen = aType === "PHYS" ? (aStats.armor_pierce || 0) : (aStats.magic_pierce || 0);
    const pctPen = aType === "PHYS" ? (aStats.armor_pierce_pct || 0) : (aStats.magic_pierce_pct || 0);
    return applyDefenseWithPenetration(raw, def, flatPen, pctPen);
  }, [aType, aStats, bStats]);

  const bPerHit = useMemo(() => {
    const raw = bStats.phys_atk;
    return applyDefense(raw, aStats.phys_def);
  }, [bStats, aStats]);

  const speed = Math.max(0.1, +atkSpeed || 1);
  const aDps = aPerHit * speed;
  const bDps = bPerHit * speed;

  const aTTK = aDps > 0 ? (bStats.hp / aDps) : Infinity;
  const bTTK = bDps > 0 ? (aStats.hp / bDps) : Infinity;

  const winner = useMemo(() => {
    if (!isFinite(aTTK) && !isFinite(bTTK)) return "—";
    if (aTTK === bTTK) return "Draw";
    return aTTK < bTTK ? "A wins" : "B wins";
  }, [aTTK, bTTK]);

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <Card neon style={{ padding: 18 }}>
        <ScanLine />
        <CornerAccents />

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(420px, 1fr) minmax(360px, 520px)",
            gap: 14,
            alignItems: "start",
          }}
        >
          {/* LEFT: Controls */}
          <div style={{ display: "grid", gap: 12 }}>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
              <HeroSelector heroes={heroes} value={aHeroName} onChange={(e) => setAHeroName(e.target.value)} label="Hero A" getHeroImageUrl={getHeroImageUrl}
                showRoleFilter={true}
                maxHeight={400} />
              <LevelSelect value={aLv} onChange={(e) => setALv(+e.target.value)} label="A Lv" />
              <HeroSelector heroes={heroes} value={bHeroName} onChange={(e) => setBHeroName(e.target.value)} label="Hero B" getHeroImageUrl={getHeroImageUrl}
                showRoleFilter={true}
                maxHeight={400} />
              <LevelSelect value={bLv} onChange={(e) => setBLv(+e.target.value)} label="B Lv" />
            </div>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <div style={{ width: 260 }}>
                <div style={{ color: "#9ca3af", fontWeight: "bold", marginBottom: 6 }}>A Attack Type</div>
                <Select
                  value={aType}
                  onChange={(e) => setAType(e.target.value)}
                  options={[
                    { value: "PHYS", label: "Physical (use ATK)" },
                    { value: "MAGIC", label: "Magic (use AP)" },
                  ]}
                />
              </div>

              <div style={{ minWidth: 260, flex: 1 }}>
                <div style={{ color: "#9ca3af", fontWeight: "bold", marginBottom: 6 }}>Attack Speed (hits/sec, simple)</div>
                <Input value={atkSpeed} onChange={(e) => setAtkSpeed(e.target.value)} placeholder="เช่น 1.0" icon={<Wind size={16} />} />
              </div>
            </div>

            <div style={{ color: "#9ca3af", fontSize: 13 }}>
              * Simulator แบบง่าย: ใช้ค่า ATK/AP + DEF และ atkSpeed ที่คุณกำหนด
            </div>
          </div>

          {/* RIGHT: Result */}
          <Card style={{ padding: 14 }}>
            <div style={{ fontWeight: "bold", color: "#ffd7dc", marginBottom: 10 }}>Result</div>

            <div style={{ display: "grid", gap: 10 }}>
              <div style={{ color: "#9ca3af" }}>
                A per-hit:{" "}
                <span style={{ color: "#fff", fontFamily: "monospace", fontWeight: "bold" }}>
                  {Math.round(aPerHit).toLocaleString()}
                </span>{" "}
                • DPS:{" "}
                <span style={{ color: "#ffd7dc", fontFamily: "monospace", fontWeight: "bold" }}>
                  {Math.round(aDps).toLocaleString()}
                </span>{" "}
                • TTK:{" "}
                <span style={{ color: "#22c55e", fontFamily: "monospace", fontWeight: "bold" }}>
                  {isFinite(aTTK) ? aTTK.toFixed(2) : "∞"}s
                </span>
              </div>

              <div style={{ color: "#9ca3af" }}>
                B per-hit:{" "}
                <span style={{ color: "#fff", fontFamily: "monospace", fontWeight: "bold" }}>
                  {Math.round(bPerHit).toLocaleString()}
                </span>{" "}
                • DPS:{" "}
                <span style={{ color: "#ffd7dc", fontFamily: "monospace", fontWeight: "bold" }}>
                  {Math.round(bDps).toLocaleString()}
                </span>{" "}
                • TTK:{" "}
                <span style={{ color: "#22c55e", fontFamily: "monospace", fontWeight: "bold" }}>
                  {isFinite(bTTK) ? bTTK.toFixed(2) : "∞"}s
                </span>
              </div>

              <div style={{ marginTop: 6, fontWeight: "bold", color: "#fff" }}>
                Winner:{" "}
                <span style={{ color: winner === "A wins" ? "#22c55e" : winner === "B wins" ? "#ef4444" : "#ffd7dc" }}>
                  {winner}
                </span>
              </div>
            </div>
          </Card>
        </div>
      </Card>


      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(820px, 1fr))", gap: 14 }}>
        <ItemShopPicker items={items} selected={aItems} setSelected={setAItems} title="Hero A Items" compact />
        <ItemShopPicker items={items} selected={bItems} setSelected={setBItems} title="Hero B Items" compact />

      </div>


      <Card style={{ padding: 16 }}>
        <div style={{ fontWeight: "bold", color: "#ffd7dc", marginBottom: 10 }}>Overall Stats (A vs B)</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12 }}>
          {BASE_FIELDS.map((k) => {
            const def = STATS[k];
            const Icon = def.i;
            const av = aStats[k] || 0;
            const bv = bStats[k] || 0;
            const delta = av - bv;
            return (
              <Card key={k} style={{ padding: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, alignItems: "center" }}>
                  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <Icon size={18} color={def.c} />
                    <div style={{ fontWeight: "bold" }}>{def.l}</div>
                  </div>
                  <div style={{ color: delta >= 0 ? "#22c55e" : "#ef4444", fontFamily: "monospace", fontWeight: "bold" }}>
                    {delta >= 0 ? "+" : ""}{Math.round(delta).toLocaleString()}
                  </div>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", color: "#9ca3af", fontFamily: "monospace" }}>
                  <span>A: {Math.round(av).toLocaleString()}</span>
                  <span>B: {Math.round(bv).toLocaleString()}</span>
                </div>
              </Card>
            );
          })}
        </div>
      </Card>
    </div>
  );
};

// ===================== Dashboard =====================
const Dashboard = ({ patches, idx, setIdx, compare, setCompare, onAdd, onReset }) => {
  const [tab, setTab] = useState("overview");
  const [uxMode, setUxMode] = useState("player"); // "player" | "analyst"

  const data = patches[idx];

  const patchOptions = useMemo(() => patches.map((p, i) => ({ value: i, label: `${i + 1}. ${p.meta.patchName}` })), [patches]);

  useEffect(() => {
    if (!patches.length) return;
    if (idx < 0 || idx >= patches.length) setIdx(0);
  }, [patches, idx, setIdx]);

  const tabs = [
    { key: "overview", label: "Overall Stat", icon: BarChart3 },
    { key: "heroCompare", label: "Hero Compare", icon: GitCompare },
    { key: "compare", label: "Patch Compare", icon: GitCompare },
    { key: "gallery", label: "Hero Gallery", icon: Users },
    { key: "items", label: "Items + Price", icon: FileSpreadsheet },
    { key: "damage", label: "Damage Calculator", icon: Calculator },
    { key: "progress", label: "Level Progression", icon: TrendingUp },
    { key: "combat", label: "Combat Simulator", icon: Sword },  // ← เพิ่มใหม่
  ];

  return (
    <>
      <GlobalStyles />
      <div style={{ minHeight: "100vh", background: "#000000", color: "#ffffff", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "fixed", inset: 0, pointerEvents: "none", overflow: "hidden" }}>
          <Glow c="#ff1026" s={800} x="-20%" y="-10%" />
          <Glow c="#ff4d5f" s={600} x="80%" y="70%" />
          <Glow c="#f5f5f5" s={400} x="50%" y="50%" />
          <Grid />
          <HexPattern />
        </div>

        <header
          style={{
            position: "sticky",
            top: 0,
            zIndex: 40,
            background: "linear-gradient(to bottom, rgba(0, 0, 0, 0.95), rgba(0, 0, 0, 0.8))",
            backdropFilter: "blur(20px)",
            borderBottom: "1px solid rgba(255,16,38,0.3)",
            boxShadow: "0 10px 40px rgba(255,16,38,0.2)",
          }}
        >
          <div
            style={{
              maxWidth: "1700px",
              margin: "0 auto",
              padding: "14px 20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <div style={{ position: "relative" }}>
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: "linear-gradient(135deg, #ff1026, #a30013)",
                    borderRadius: "12px",
                    filter: "blur(16px)",
                    opacity: 0.6,
                    animation: "pulse 2s ease-in-out infinite",
                  }}
                />
                <div
                  style={{
                    position: "relative",
                    width: "52px",
                    height: "52px",
                    background: "linear-gradient(135deg, #ff1026, #f5f5f5)",
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 0 30px rgba(255,16,38,0.8)",
                  }}
                >
                  <BrandLogoBadge size={52} radius={10} padding={4} />
                </div>
              </div>

              <div>
                <h1
                  style={{
                    fontFamily: "'Orbitron', sans-serif",
                    fontSize: "1.4rem",
                    fontWeight: 900,
                    background: "linear-gradient(to right, #ff1026, #f5f5f5)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                    letterSpacing: "0.25em",
                    filter: "drop-shadow(0 0 20px rgba(255,16,38,0.8))",
                  }}
                >
                  META LAB
                </h1>
                <p style={{ fontSize: "0.75rem", color: "#6b7280", letterSpacing: "0.1em" }}>
                  {data?.meta?.patchName || "—"}
                </p>
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginLeft: "auto" }}>
              <div style={{ minWidth: 280 }}>
                <Select
                  value={idx}
                  onChange={(e) => setIdx(+e.target.value)}
                  options={patchOptions}
                  icon={<Database size={16} />}
                />
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "10px 14px",
                  background: "rgba(0, 0, 0, 0.6)",
                  borderRadius: "12px",
                  border: "2px solid rgba(255,16,38,0.3)",
                  fontSize: "0.875rem",
                  boxShadow: "0 0 20px rgba(255,16,38,0.2)",
                }}
              >
                <Database size={16} color="#ff1026" />
                <span style={{ color: "#9ca3af", fontWeight: "bold" }}>
                  {data?.meta?.heroCount || 0} Heroes • {data?.meta?.itemCount || 0} Items • {data?.meta?.skillCount || 0} Skills
                </span>
              </div>

              <Button onClick={onAdd} variant="secondary" style={{ width: 46, height: 46, padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Plus size={20} />
              </Button>
              <Button onClick={onReset} variant="secondary" style={{ width: 46, height: 46, padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <RefreshCw size={20} />
              </Button>
            </div>
          </div>

          <div style={{ maxWidth: "1700px", margin: "0 auto", padding: "10px 20px 14px" }}>
            <div className="scroll-container" style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 6 }}>
              {tabs.map((t) => (
                <TabBtn key={t.key} active={tab === t.key} icon={t.icon} label={t.label} onClick={() => setTab(t.key)} />
              ))}
            </div>
          </div>
        </header>

        <main style={{ position: "relative", maxWidth: "1700px", margin: "0 auto", padding: "24px 20px 44px" }}>
          {tab === "overview" && <OverallStatTab patch={data} />}
          {tab === "items" && <ItemsTab patch={data} />}
          {tab === "gallery" && <HeroGalleryTab patch={data} />}
          {tab === "damage" && <DamageCalculatorTab patch={data} />}
          {tab === "progress" && <LevelProgressionTab patch={data} />}
          {tab === "combat" && (
            <CombatSimulatorTab
              patch={data}
              getHeroImageUrl={getHeroImageUrl}
              HeroSelector={HeroSelector}
              ItemShopPicker={ItemShopPicker}
            />
          )}

          {/* ========== ฟีเจอร์ใหม่ ========== */}

          {tab === "heroCompare" && (
            <HeroCompareTab
              patch={data}
              getHeroImageUrl={getHeroImageUrl}
              HeroSelector={HeroSelector}
              ItemShopPicker={ItemShopPicker}   // ✅ เพิ่มบรรทัดนี้
            />
          )}


          {tab === "combo" && (
            <SkillComboCalculator
              patch={data}
              getHeroImageUrl={getHeroImageUrl}
              HeroSelector={HeroSelector}
              ItemShopPicker={ItemShopPicker}   // ✅ เพิ่มบรรทัดนี้
            />
          )}


          {tab === "compare" && (
            <EnhancedPatchCompareV2
              patches={patches}
              aIdx={compare.a}
              bIdx={compare.b}
              setAIdx={(v) => setCompare(prev => ({ ...prev, a: v }))}
              setBIdx={(v) => setCompare(prev => ({ ...prev, b: v }))}
              getHeroImageUrl={getHeroImageUrl}
              getItemImageCandidates={getItemImageCandidates}
              HeroSelector={HeroSelector}
            />
          )}

          {/* ========== จบฟีเจอร์ใหม่ ========== */}
        </main>
      </div>
    </>
  );
};
const loadDefaultPatchFromPublic = async ({ patchName = "DEFAULT" } = {}) => {
  const res = await fetch(`${process.env.PUBLIC_URL}/metalab/rov-database.xlsx`);
  if (!res.ok) throw new Error("Default MetaLab Excel not found in /public/metalab");

  const buf = await res.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });

  // *** ยก logic parse จาก UploadScreen.parse(f) มาใช้ตรงนี้ ***
  const d = { heroes: [], skills: [], items: [], runes: [] };

  // Heroes
  // ค้นหาส่วน if (wb.SheetNames.includes("Heroes")) ใน MetaLab.jsx
  if (wb.SheetNames.includes("Heroes")) {
    const j = XLSX.utils.sheet_to_json(wb.Sheets["Heroes"], { header: 1 });
    // เริ่มวนลูปที่ i = 2 เพราะแถวที่ 0 และ 1 คือ Header ภาษาอังกฤษและไทย
    for (let i = 2; i < j.length; i++) {
      const r = j[i];

      // 1. ล้างช่องว่างหน้าและหลังชื่อฮีโร่ ป้องกันปัญหา "Airi " (มีเว้นวรรค)
      const hName = String(r[0] || "").trim();

      // 2. ข้ามบรรทัดถ้าไม่มีชื่อฮีโร่ หรือเป็นบรรทัดหัวข้อ
      if (!hName || hName === "ชื่อฮีโร่" || hName === "hero_name") continue;

      d.heroes.push({
        name: hName,
        role: String(r[1] || "").trim(),
        lane: String(r[2] || "").trim(),
        // 3. ใช้ parseInt และ parseFloat เพื่อบังคับให้เป็นตัวเลข ป้องกันค่าที่เป็นช่องว่าง (Empty String)
        level: parseInt(r[3]) || 1,
        hp: parseFloat(r[4]) || 0,
        phys_atk: parseFloat(r[6]) || 0,
        magic_power: parseFloat(r[8]) || 0,
        phys_def: parseFloat(r[9]) || 0,
        phys_def_pct: parseFloat(r[11]) || 0,
        magic_def: parseFloat(r[13]) || 0,
        magic_def_pct: parseFloat(r[15]) || 0,
        max_mana: parseFloat(r[17]) || 0,
        move_speed: parseFloat(r[19]) || 0,
        armor_pierce: parseFloat(r[20]) || 0,
        armor_pierce_pct: parseFloat(r[21]) || 0,
        magic_pierce: parseFloat(r[22]) || 0,
        magic_pierce_pct: parseFloat(r[23]) || 0,
        attack_speed: parseFloat(r[24]) || 0,
        crit_rate: parseFloat(r[25]) || 0,
        crit_dmg: parseFloat(r[26]) || 0,
        life_steal: parseFloat(r[27]) || 0,
        magic_life_steal: parseFloat(r[28]) || 0,
        cooldown_reduction: parseFloat(r[29]) || 0,
      });
    }
  }

  // Skills
  if (wb.SheetNames.includes("Skills")) {
    const j = XLSX.utils.sheet_to_json(wb.Sheets["Skills"], { header: 1 });
    for (let i = 2; i < j.length; i++) {
      const r = j[i];
      r[0] &&
        r[2] &&
        d.skills.push({
          hero: r[0],
          slot: r[1] || "",
          name: r[2] || "",
          type: r[3] || "",
          damage_type: String(r[4] || ""),
          description: r[5] || "",
          base_dmg: [r[6], r[7], r[8], r[9], r[10], r[11]].map((v) => +v || 0),
          ad_scaling: +r[12] || 0,
          ap_scaling: +r[13] || 0,
          cooldown: [r[14], r[15], r[16], r[17], r[18], r[19]].map((v) => +v || 0),
          mana_cost: [r[20], r[21], r[22], r[23], r[24], r[25]].map((v) => +v || 0),
          range: r[26] || "",
          cc_type: r[27] || "",
          cc_duration: +r[28] || 0,
          additional_effects: r[29] || "",
        });
    }
  }

  // Arcana
  // เปลี่ยน section Arcana ใน loadDefaultPatchFromPublic เป็น:
  if (wb.SheetNames.includes("Arcana")) {
    const sheet = wb.Sheets["Arcana"];
    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });

    const parseNum = (v) => {
      if (typeof v === "number") return v;
      const s = String(v || "").replace(/[+%]/g, "").trim();
      return parseFloat(s) || 0;
    };
    const addStat = (stats, key, val) => { if (val > 0) stats[key] = val; };

    for (let i = 2; i < rows.length; i++) {
      const r = rows[i];
      if (!r[0]) continue;

      const stats = {};
      addStat(stats, "hp", parseNum(r[4]));
      addStat(stats, "phys_atk", parseNum(r[6]));
      addStat(stats, "magic_power", parseNum(r[8]));
      addStat(stats, "phys_def", parseNum(r[10]));
      addStat(stats, "magic_def", parseNum(r[12]));
      addStat(stats, "move_speed_pct", parseNum(r[15]));
      addStat(stats, "armor_pierce", parseNum(r[16]));
      addStat(stats, "magic_pierce", parseNum(r[18]));
      addStat(stats, "attack_speed", parseNum(r[20]));
      addStat(stats, "crit_rate", parseNum(r[21]));
      addStat(stats, "crit_dmg", parseNum(r[22]));
      addStat(stats, "life_steal", parseNum(r[23]));
      addStat(stats, "magic_life_steal", parseNum(r[24]));
      addStat(stats, "cooldown_reduction", parseNum(r[25]));

      d.runes.push({
        type: "ARCANA",
        name: r[0],
        tier: parseNum(r[2]),
        color: r[1],  // Red/Purple/Green
        desc: r[3],
        stats,
      });
    }
  }

  // Items (ของคุณเป็น fixed index)
  if (wb.SheetNames.includes("Items")) {
    const sheet = wb.Sheets["Items"];
    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });

    const parseNum = (v) => {
      if (v === null || v === undefined || v === "") return 0;
      if (typeof v === "number") return v;
      const s = String(v).trim();
      if (s.includes("/")) return parseFloat(s.split("/")[0]) || 0;
      const cleaned = s.replace(/[+%]/g, "").trim();
      const n = parseFloat(cleaned);
      return Number.isFinite(n) ? n : 0;
    };
    const pct = (v) => { const n = parseNum(v); return n < 1 && n > 0 ? n * 100 : n; };

    const COL = {
      name: 0, category: 1, tier: 2, price: 3, sell_price: 4, build_from: 5,
      hp: 6, passive_inline: 7, phys_atk: 8, attack_speed: 9, critical_rate: 10,
      life_steal: 11, magic_power: 12, phys_defense: 13, magic_defense: 14,
      max_mana: 15, movement_speed: 16, movement_speed_pct: 17,
      armor_pierce: 18, armor_pierce_pct: 19, magic_pierce: 20, magic_pierce_pct: 21,
      cooldown_reduction: 27, hp_regen: 28, mana_regen: 29,
      passive_name: 30, passive_desc: 31,
    };

    for (let i = 2; i < rows.length; i++) {
      const r = rows[i];
      const name = String(r[COL.name] || "").trim();
      if (!name) continue;

      const stats = {
        hp: parseNum(r[COL.hp]),
        phys_atk: parseNum(r[COL.phys_atk]),
        attack_speed: pct(r[COL.attack_speed]),
        crit_rate: pct(r[COL.critical_rate]),
        life_steal: pct(r[COL.life_steal]),
        magic_power: parseNum(r[COL.magic_power]),
        phys_def: parseNum(r[COL.phys_defense]),
        magic_def: parseNum(r[COL.magic_defense]),
        max_mana: parseNum(r[COL.max_mana]),
        move_speed: parseNum(r[COL.movement_speed]),
        move_speed_pct: pct(r[COL.movement_speed_pct]),
        armor_pierce: parseNum(r[COL.armor_pierce]),
        armor_pierce_pct: pct(r[COL.armor_pierce_pct]),
        magic_pierce: parseNum(r[COL.magic_pierce]),
        magic_pierce_pct: pct(r[COL.magic_pierce_pct]),
        cooldown_reduction: pct(r[COL.cooldown_reduction]),
        hp_regen: parseNum(r[COL.hp_regen]),
        mana_regen: parseNum(r[COL.mana_regen]),
      };

      const passiveInline = String(r[COL.passive_inline] || "").trim();
      const passiveDesc = String(r[COL.passive_desc] || "").trim();

      d.items.push({
        name,
        category: String(r[COL.category] || "").trim(),
        tier: parseNum(r[COL.tier]) || 1,
        price: parseNum(r[COL.price]),
        sell_price: parseNum(r[COL.sell_price]),
        build_from: String(r[COL.build_from] || "").trim(),
        image: "",
        stats,
        passive_name: String(r[COL.passive_name] || "").trim(),
        passive: passiveInline || passiveDesc,
      });
    }
  }

  // Runes (เดิมของคุณ)
  if (wb.SheetNames.includes("Runes")) {
    const j = XLSX.utils.sheet_to_json(wb.Sheets["Runes"], { header: 1 });
    for (let i = 2; i < j.length; i++) {
      const r = j[i];
      r[0] &&
        d.runes.push({
          name: r[0],
          tree: r[1] || "",
          tier: +r[2] || 1,
          desc: r[3] || "",
        });
    }
  }

  // Group heroes -> levels map (เหมือนเดิม)
  const hm = new Map();
  d.heroes.forEach((h) => {
    if (!hm.has(h.name)) hm.set(h.name, { name: h.name, role: h.role, lane: h.lane, levels: {} });
    hm.get(h.name).levels[h.level] = { ...h };
  });
  addFlowbornPatchHeroes(hm);

  // เติมข้อมูลเลเวลที่ว่างด้วยการคำนวณจาก Lv1/Lv2 (กรณีใน Excel มีเฉพาะ 1-2)
  hm.forEach((hero) => fillMissingHeroLevels(hero));

  const pd = {
    heroes: [...hm.values()],
    skills: d.skills,
    items: d.items,
    runes: d.runes,
    meta: {
      heroCount: hm.size,
      skillCount: d.skills.length,
      itemCount: d.items.length,
      runeCount: d.runes.length,
      patchName,
    },
  };

  return pd;
};

// ===================== MetaLab (App root) =====================
export default function MetaLab() {
  const [patches, setPatches] = useState([]);
  const [idx, setIdx] = useState(0);
  const [upload, setUpload] = useState(false); // ✅ เปลี่ยน default เป็น false
  const [compare, setCompare] = useState({ a: 0, b: 1 });
  const [bootErr, setBootErr] = useState(null);

  useEffect(() => {
    // Register hero passives ครั้งเดียวตอน mount
    registerAllHeroPassives();
    console.log('[CombatSimulator] Hero passives registered');
  }, []);

  // ✅ โหลด default patch อัตโนมัติ 1 ครั้ง
  useEffect(() => {
    (async () => {
      try {
        const pd = await loadDefaultPatchFromPublic({ patchName: "DEFAULT" });
        setPatches([pd]);
        setIdx(0);
        setCompare({ a: 0, b: 0 });
      } catch (e) {
        console.error(e);
        setBootErr("ไม่พบไฟล์ default ใน /public/metalab หรือโหลดไม่ได้");
        setUpload(true); // fallback ให้เข้า upload ได้
      }
    })();
  }, []);

  const load = (d) => {
    setPatches((prev) => {
      const next = [...prev, d];
      const newIdx = next.length - 1;
      setIdx(newIdx);
      if (next.length >= 2) setCompare({ a: next.length - 2, b: next.length - 1 });
      return next;
    });
    setUpload(false);
  };

  const add = () => setUpload(true);
  const reset = () => {
    setPatches([]);
    setIdx(0);
    setCompare({ a: 0, b: 1 });
    setUpload(true);
  };

  if (bootErr && upload) return <UploadScreen onLoad={load} patches={patches} />;

  if (upload) return <UploadScreen onLoad={load} patches={patches} />;

  // ✅ ถ้ายังไม่โหลด patches (ระหว่าง fetch) ให้โชว์ loading เบา ๆ
  if (!patches.length) return <div style={{ color: "#fff", padding: 24 }}>Loading default MetaLab patch...</div>;

  return (
    <Dashboard
      patches={patches}
      idx={idx}
      setIdx={setIdx}
      compare={compare}
      setCompare={setCompare}
      onAdd={add}
      onReset={reset}
    />
  );
}
